// Curated bilingual question bank for /games/questions. Each entry:
//   id        stable string used in the DB (question_id column)
//   category  bucket for grouping / future deck UI
//   text      { en, ar } — the question itself
//   options   array of { id, text:{en, ar} } — what the user can tap
//
// Daily selection: hashDateKey(localDateKey()) % BANK.length, same as
// Wordle. The bank is small enough (~50) that the cycle repeats in
// under two months — that's intentional for a "let me discover what
// the partner likes" feature where seeing the same question twice (and maybe
// answering differently the second time) is part of the point.
//
// Order is not alphabetical so the day-hash distributes daily picks
// across categories.

export const CATEGORIES = ['preferences', 'dreams', 'goofy', 'memories', 'spicy', 'deep', 'intimate']

const BANK = [
  {
    id: 'fav-season',
    category: 'preferences',
    text: { en: 'Favourite season?', ar: 'إيه أكتر فصل في السنة بتحبه؟' },
    options: [
      { id: 'spring', text: { en: 'Spring', ar: 'الربيع والجو الرايق' } },
      { id: 'summer', text: { en: 'Summer', ar: 'الصيف والبحر' } },
      { id: 'fall', text: { en: 'Fall', ar: 'الخريف الشاعري' } },
      { id: 'winter', text: { en: 'Winter', ar: 'الشتا والكتكتة' } },
    ],
  },
  {
    id: 'fri-night',
    category: 'preferences',
    text: { en: 'Perfect Friday night?', ar: 'ليلة الجمعة المثالية بالنسبالك؟' },
    options: [
      { id: 'stay-in', text: { en: 'Stay in, chill', ar: 'أنتخ في البيت وأفصل' } },
      { id: 'dinner', text: { en: 'Dinner out', ar: 'خروجة عشا برّه' } },
      { id: 'movie', text: { en: 'Movie', ar: 'سهرة سينما' } },
      { id: 'adventure', text: { en: 'Spontaneous', ar: 'أي حاجة تطلع فجأة' } },
    ],
  },
  {
    id: 'travel-vibe',
    category: 'preferences',
    text: { en: 'Where would you rather travel?', ar: 'لو هنسافر دلوقتي، تختار فين؟' },
    options: [
      { id: 'mountains', text: { en: 'Mountains', ar: 'جبال ومغامرة' } },
      { id: 'beach', text: { en: 'Beach', ar: 'بحر وتشيلنج' } },
      { id: 'city', text: { en: 'Big city', ar: 'مدينة زحمة وحياة' } },
      { id: 'desert', text: { en: 'Desert', ar: 'سفاري وصحرا' } },
    ],
  },
  {
    id: 'drink',
    category: 'preferences',
    text: { en: 'Pick a drink', ar: 'مشروبك الرسمي إيه؟' },
    options: [
      { id: 'coffee', text: { en: 'Coffee', ar: 'قهوة طبعاً' } },
      { id: 'tea', text: { en: 'Tea', ar: 'شاي متين' } },
      { id: 'juice', text: { en: 'Juice', ar: 'عصير فريش' } },
      { id: 'water', text: { en: 'Water', ar: 'مية ساقعة' } },
    ],
  },
  {
    id: 'sweet-savory',
    category: 'preferences',
    text: { en: 'Sweet or savoury?', ar: 'مودك بيميل أكتر لـ إيه؟' },
    options: [
      { id: 'sweet', text: { en: 'Sweet', ar: 'حاجة حلوة' } },
      { id: 'savory', text: { en: 'Savoury', ar: 'حوادق وموالح' } },
      { id: 'both', text: { en: 'Both', ar: 'مكس بين الاتنين' } },
    ],
  },
  {
    id: 'morning-type',
    category: 'preferences',
    text: { en: 'Early bird or night owl?', ar: 'أنت كائن إيه بالظبط؟' },
    options: [
      { id: 'early', text: { en: 'Early bird', ar: 'عصفور صباحي بنشاطه' } },
      { id: 'night', text: { en: 'Night owl', ar: 'بومة ليلية بتعشق الهدوء' } },
      { id: 'depends', text: { en: 'Depends', ar: 'حسب المود واليوم' } },
    ],
  },
  {
    id: 'weather',
    category: 'preferences',
    text: { en: 'Favourite weather?', ar: 'أنهي جو بيظبط مودك؟' },
    options: [
      { id: 'sunny', text: { en: 'Sunny', ar: 'شمس وتنوير' } },
      { id: 'rainy', text: { en: 'Rainy', ar: 'مطرة وكوباية شاي' } },
      { id: 'cloudy', text: { en: 'Cloudy', ar: 'مغيم وشاعري' } },
      { id: 'breezy', text: { en: 'Breezy', ar: 'فيه نسمة هواء طراوة' } },
    ],
  },
  {
    id: 'movies-or-books',
    category: 'preferences',
    text: { en: 'Movies or books?', ar: 'أفلام وسينما ولا كتب وقراية؟' },
    options: [
      { id: 'movies', text: { en: 'Movies', ar: 'أفلام وعيش التجربة' } },
      { id: 'books', text: { en: 'Books', ar: 'كتب وخيال مفتوح' } },
      { id: 'both', text: { en: 'Both', ar: 'الاتنين مكملين لبعض' } },
    ],
  },
  {
    id: 'plan-style',
    category: 'preferences',
    text: { en: 'Plan ahead or spontaneous?', ar: 'دنيتك بتمشي إزاي؟' },
    options: [
      { id: 'planner', text: { en: 'I plan', ar: 'بالورقة والقلم وبخطط' } },
      { id: 'spontaneous', text: { en: 'I improvise', ar: 'سبونتينوس وعلى الله حكايتها' } },
      { id: 'mix', text: { en: 'A bit of both', ar: 'مكس بين العقل والجنون' } },
    ],
  },
  {
    id: 'pet',
    category: 'preferences',
    text: { en: 'Dream pet?', ar: 'حيوان أليف نفسك تربيه؟' },
    options: [
      { id: 'cat', text: { en: 'Cat', ar: 'قطة مستقلة بحالها' } },
      { id: 'dog', text: { en: 'Dog', ar: 'كلب وفي ومبتفارقوش' } },
      { id: 'fish', text: { en: 'Fish', ar: 'حوض سمك هادي' } },
      { id: 'none', text: { en: 'No pet, thanks', ar: 'مستحيل أدخل حيوان بيتي' } },
    ],
  },
  {
    id: 'live-where',
    category: 'dreams',
    text: { en: 'Dream city to live in?', ar: 'مدينة نفسك تعيش فيها؟' },
    options: [
      { id: 'cairo', text: { en: 'Cairo', ar: 'القاهرة الساهرة' } },
      { id: 'dubai', text: { en: 'Dubai', ar: 'دبي المستقبل' } },
      { id: 'london', text: { en: 'London', ar: 'لندن والضباب' } },
      { id: 'other', text: { en: 'Somewhere else', ar: 'في حتة تانية خالص' } },
    ],
  },
  {
    id: 'home-shape',
    category: 'dreams',
    text: { en: 'Ideal home?', ar: 'بيت أحلامك شكله إيه؟' },
    options: [
      { id: 'apartment', text: { en: 'High-rise apartment', ar: 'شقة مودرن في برج عالي' } },
      { id: 'house', text: { en: 'Cosy house', ar: 'بيت صغير ودافي' } },
      { id: 'villa', text: { en: 'Villa with a garden', ar: 'فيلا رايقة بجنينة' } },
      { id: 'cabin', text: { en: 'Cabin in nature', ar: 'كوخ معزول وسط الطبيعة' } },
    ],
  },
  {
    id: 'kids',
    category: 'dreams',
    text: { en: 'Kids someday?', ar: 'نفسك يبقى عندك أطفال قدام؟' },
    options: [
      { id: 'none', text: { en: 'No, thanks', ar: 'لأ مش في حسناتي' } },
      { id: 'one', text: { en: 'One', ar: 'طفل واحد كفاية' } },
      { id: 'two', text: { en: 'Two', ar: 'اتنين عشان يسلوا بعض' } },
      { id: 'three+', text: { en: 'Three+', ar: 'عزوة.. تلاتة أو أكتر' } },
    ],
  },
  {
    id: 'super-power',
    category: 'dreams',
    text: { en: 'Pick a superpower', ar: 'لو هتاخد Superpower واحدة، تختار إيه؟' },
    options: [
      { id: 'fly', text: { en: 'Fly', ar: 'الطيران والحرية' } },
      { id: 'invisible', text: { en: 'Invisibility', ar: 'الاختفاء والروقان' } },
      { id: 'mind', text: { en: 'Read minds', ar: 'قراءة أفكار الناس' } },
      { id: 'time', text: { en: 'Time travel', ar: 'السفر عبر الزمن' } },
    ],
  },
  {
    id: 'vacation-style',
    category: 'dreams',
    text: { en: 'Vacation budget — pick a vibe', ar: 'لو بتخطط لإجازة، هتمشيها إزاي؟' },
    options: [
      { id: 'luxury-short', text: { en: 'Luxury, short', ar: 'فخامة وتدليع بس أيام قليلة' } },
      { id: 'budget-long', text: { en: 'Budget, long', ar: 'على الهادي واقتصادية بس مدة طويلة' } },
      { id: 'mid', text: { en: 'A balance', ar: 'خير الأمور الوسط' } },
    ],
  },
  {
    id: 'next-year',
    category: 'dreams',
    text: { en: 'One word for next year?', ar: 'كلمة واحدة بتتمناها للسنة الجاية؟' },
    options: [
      { id: 'growth', text: { en: 'Growth', ar: 'عاش ونمو' } },
      { id: 'rest', text: { en: 'Rest', ar: 'هدوء وراحة بال' } },
      { id: 'change', text: { en: 'Change', ar: 'تغيير وتجديد' } },
      { id: 'us', text: { en: 'Us', ar: 'نبقى مع بعض' } },
    ],
  },
  {
    id: 'snack-2am',
    category: 'goofy',
    text: { en: 'Snack at 2am?', ar: 'الساعة 2 بالليل، إيه الجريمة اللي بتعملها؟' },
    options: [
      { id: 'chips', text: { en: 'Chips', ar: 'كيس شيبسي وفصلان' } },
      { id: 'chocolate', text: { en: 'Chocolate', ar: 'حاجة مسكرة ترفع السيروتونين' } },
      { id: 'fruit', text: { en: 'Fruit', ar: 'فاكهة عشان ضميري ميتعبنيش' } },
      { id: 'nothing', text: { en: 'Nothing, sleeping', ar: 'أنا بكون في سابع نومة' } },
    ],
  },
  {
    id: 'animal',
    category: 'goofy',
    text: { en: 'If you were an animal?', ar: 'لو صحيت ولقيت نفسك حيوان، هتبقي إيه؟' },
    options: [
      { id: 'cat', text: { en: 'Cat', ar: 'قطة مدلوقة وبتروق على حالها' } },
      { id: 'lion', text: { en: 'Lion', ar: 'أسد كينج في مكاني' } },
      { id: 'bird', text: { en: 'Bird', ar: 'طير طاير في ملكوت الله' } },
      { id: 'dolphin', text: { en: 'Dolphin', ar: 'دولفين فرفوش وبحب اللعب' } },
    ],
  },
  {
    id: 'karaoke',
    category: 'goofy',
    text: { en: 'Karaoke pick?', ar: 'لو المايك في إيدك في الكاريوكي، هتغني إيه؟' },
    options: [
      { id: 'pop', text: { en: 'Pop', ar: 'أغنية بوب حماسية' } },
      { id: 'rock', text: { en: 'Rock', ar: 'حاجة روك أهز بيها المكان' } },
      { id: 'arabic', text: { en: 'Arabic', ar: 'أغنية عربي كل الحاضرين حافظينها' } },
      { id: 'no-sing', text: { en: 'Won\'t sing', ar: 'هتفرج عليكم وأنا ساكت' } },
    ],
  },
  {
    id: 'color',
    category: 'goofy',
    text: { en: 'Favourite colour?', ar: 'لو حياتك لون واحد، هتنور بأنهي لون؟' },
    options: [
      { id: 'red', text: { en: 'Red', ar: 'أحمر جريء' } },
      { id: 'blue', text: { en: 'Blue', ar: 'أزرق رايق زي البحر' } },
      { id: 'green', text: { en: 'Green', ar: 'أخضر مريح للأعصاب' } },
      { id: 'black', text: { en: 'Black', ar: 'أسود فخم' } },
      { id: 'purple', text: { en: 'Purple', ar: 'بنفسجي مختلف' } },
    ],
  },
  {
    id: 'pizza',
    category: 'goofy',
    text: { en: 'Pizza topping?', ar: 'لو طالب بيتزا، إيه التوبينج الأساسي عندك؟' },
    options: [
      { id: 'cheese', text: { en: 'Cheese', ar: 'ميكس جبن سايح' } },
      { id: 'pepperoni', text: { en: 'Pepperoni', ar: 'بيبروني غرقان صوص' } },
      { id: 'veggie', text: { en: 'Veggies', ar: 'خضار عشان الفريش' } },
      { id: 'pineapple', text: { en: 'Pineapple', ar: 'أناناس (الناس الأذكياء بس)' } },
    ],
  },
  {
    id: 'shoes',
    category: 'goofy',
    text: { en: 'Shoes most of the year?', ar: 'الراعي الرسمي لرجلك طول السنة؟' },
    options: [
      { id: 'sneakers', text: { en: 'Sneakers', ar: 'سنيكرز مريح وسريع' } },
      { id: 'sandals', text: { en: 'Sandals', ar: 'كروكس أو شبشب ومكبر دماغي' } },
      { id: 'boots', text: { en: 'Boots', ar: 'بوت شيك' } },
      { id: 'formal', text: { en: 'Formal', ar: 'جزمة كلاسيك لزوم الشياكة' } },
    ],
  },
  {
    id: 'shower-time',
    category: 'goofy',
    text: { en: 'Best shower time?', ar: 'أحلى وقت تاخد فيه دش وتفصل؟' },
    options: [
      { id: 'morning', text: { en: 'Morning', ar: 'الصبح عشان أفوق ليومي' } },
      { id: 'night', text: { en: 'Night', ar: 'بالليل قبل ما أرمي جسمي على السرير' } },
      { id: 'both', text: { en: 'Both', ar: 'الصبح وبالليل.. النظافة عنواني' } },
    ],
  },
  {
    id: 'phone',
    category: 'goofy',
    text: { en: 'iPhone or Android?', ar: 'المعسكر بتاعك: آيفون ولا أندرويد؟' },
    options: [
      { id: 'iphone', text: { en: 'iPhone', ar: 'آيفون والروقان' } },
      { id: 'android', text: { en: 'Android', ar: 'أندرويد والحرية' } },
    ],
  },
  {
    id: 'social',
    category: 'goofy',
    text: { en: 'Favourite social app?', ar: 'إيه أكتر أبليكيشن بيسحل وقتك؟' },
    options: [
      { id: 'ig', text: { en: 'Instagram', ar: 'انستجرام والريلز' } },
      { id: 'tt', text: { en: 'TikTok', ar: 'تيك توك وفصلان الدماغ' } },
      { id: 'x', text: { en: 'X / Twitter', ar: 'إكس وعالم الهبد' } },
      { id: 'none', text: { en: 'I avoid them', ar: 'عامل ديجيتال ديتوكس ومقاطع' } },
    ],
  },
  {
    id: 'best-month',
    category: 'memories',
    text: { en: 'Best day this month?', ar: 'لو فكرت في الشهر ده، إيه أحلى يوم عدى عليك؟' },
    options: [
      { id: 'today', text: { en: 'Today', ar: 'النهارده طبعاً' } },
      { id: 'weekend', text: { en: 'A weekend', ar: 'يوم في الويكند والانبساط' } },
      { id: 'with-you', text: { en: 'A day with you', ar: 'يوم كنا فيه مع بعض' } },
      { id: 'none', text: { en: 'Nothing stood out', ar: 'مفيتش حاجة معلمة أوي كله شبه بعضه' } },
    ],
  },
  {
    id: 'laughed',
    category: 'memories',
    text: { en: 'Last thing that made you laugh?', ar: 'فاكر آخر حاجة موتك من الضحك؟' },
    options: [
      { id: 'reel', text: { en: 'A reel', ar: 'ريل أو ميم على السوشيال ميديا' } },
      { id: 'movie', text: { en: 'A movie', ar: 'لقطة في فيلم أو مسرحية' } },
      { id: 'conversation', text: { en: 'A conversation', ar: 'قعدة حوار ومواقف مع الصحاب' } },
      { id: 'mistake', text: { en: 'A silly mistake', ar: 'موقف غبي حصل معايا' } },
    ],
  },
  {
    id: 'last-trip',
    category: 'memories',
    text: { en: 'Last trip vibe?', ar: 'آخر سفرية ليك كانت طالعة بـ أنهي مود؟' },
    options: [
      { id: 'family', text: { en: 'Family', ar: 'لمة عيلة وود' } },
      { id: 'friends', text: { en: 'Friends', ar: 'شلة الصحاب والجنون' } },
      { id: 'solo', text: { en: 'Solo', ar: 'لوحدي.. تظبيط دماغ وسفر فردي' } },
      { id: 'work', text: { en: 'Work', ar: 'سفرية شغل وضغط' } },
    ],
  },
  {
    id: 'childhood-treat',
    category: 'memories',
    text: { en: 'Childhood favourite treat?', ar: 'إيه أكتر حاجة حلوة كنت بتجري تشتريها من السوبرماركت وأنت صغير؟' },
    options: [
      { id: 'chocolate', text: { en: 'Chocolate bar', ar: 'باكو شوكولاتة معين' } },
      { id: 'icecream', text: { en: 'Ice cream', ar: 'آيس كريم في الحر' } },
      { id: 'fruit', text: { en: 'Fruit', ar: 'فاكهة ميروقة من البيت' } },
      { id: 'cookie', text: { en: 'Cookie', ar: 'بسكوت وكوكيز' } },
    ],
  },
  {
    id: 'first-job',
    category: 'memories',
    text: { en: 'First job felt like?', ar: 'فاكر أول قرش كسبته من شغلانة؟ كان إحساسك إيه؟' },
    options: [
      { id: 'exciting', text: { en: 'Exciting', ar: 'كنت طاير من الفرحة ومتحمس' } },
      { id: 'scary', text: { en: 'Scary', ar: 'توتر وخوف من المسئولية' } },
      { id: 'meh', text: { en: 'Just okay', ar: 'عادي.. شغلانة عشان الفلوس وخلاص' } },
      { id: 'na', text: { en: 'No job yet', ar: 'لسه مابدأتش رحلة الشغل' } },
    ],
  },
  {
    id: 'fav-gift',
    category: 'memories',
    text: { en: 'Last gift you loved?', ar: 'آخر هدية جاتلك ودخلت قلبك بجد؟' },
    options: [
      { id: 'material', text: { en: 'Something material', ar: 'حاجة مادية كنت نفسي فيها' } },
      { id: 'experience', text: { en: 'An experience', ar: 'خروجة أو تجربة جديدة عملناها' } },
      { id: 'words', text: { en: 'A heartfelt note', ar: 'جواب أو كلام مكتوب من القلب' } },
      { id: 'time', text: { en: 'Their time', ar: 'إنهم فضوا نفسهم عشاني مخصوص' } },
    ],
  },
  {
    id: 'love-language',
    category: 'spicy',
    text: { en: 'Your love language?', ar: 'لغة الحب الرسمية بتاعتك اللي بتفهمها؟' },
    options: [
      { id: 'words', text: { en: 'Words', ar: 'كلام الدعم والتقدير' } },
      { id: 'time', text: { en: 'Quality time', ar: 'القعدة سوا والتركيز معايا' } },
      { id: 'touch', text: { en: 'Touch', ar: 'السلام، الحضن، ولمسة الإيد' } },
      { id: 'acts', text: { en: 'Acts', ar: 'الأفعال والمواقف اللي بتبين' } },
      { id: 'gifts', text: { en: 'Gifts', ar: 'الهدايا والمفاجآت ولو بسيطة' } },
    ],
  },
  {
    id: 'ideal-date',
    category: 'spicy',
    text: { en: 'Ideal date?', ar: 'الـ Ideal Date والـ Vibe المثالي للخروجة سوا؟' },
    options: [
      { id: 'quiet-dinner', text: { en: 'Quiet dinner', ar: 'عشا رايق في مكان هادي' } },
      { id: 'adventure', text: { en: 'Adventure', ar: 'سفاري أو مغامرة فيها حركة' } },
      { id: 'cozy-home', text: { en: 'Cosy at home', ar: 'قعدة بيت ونتفرج على حاجة بنحبها' } },
      { id: 'walk', text: { en: 'A long walk', ar: 'تمشية طويلة وكلام مبيخلصش' } },
    ],
  },
  {
    id: 'romantic-gesture',
    category: 'spicy',
    text: { en: 'Most romantic gesture?', ar: 'إيه أكتر حركة رومانسية بتخطف قلبك؟' },
    options: [
      { id: 'surprise', text: { en: 'A surprise', ar: 'مفاجأة تطلع فجأة من غير مناسبة' } },
      { id: 'note', text: { en: 'A love note', ar: 'رسالة حب مكتوبة بخط الإيد' } },
      { id: 'touch', text: { en: 'Hand-holding', ar: 'ممسك إيد بعض وإحنا ماشيين' } },
      { id: 'plan', text: { en: 'A planned day', ar: 'يوم كامل متخطط ومتظبط عشاني' } },
    ],
  },
  {
    id: 'comfort-place',
    category: 'spicy',
    text: { en: 'Where you feel safest?', ar: 'إيه المكان اللي أول ما بتدخله بتحس بـ أمان وراحة؟' },
    options: [
      { id: 'home', text: { en: 'At home', ar: 'أوضتي وبيتي' } },
      { id: 'nature', text: { en: 'In nature', ar: 'وسط الطبيعة والبحر' } },
      { id: 'partner', text: { en: 'With my person', ar: 'مع الشخص اللي بحبه' } },
      { id: 'family', text: { en: 'With family', ar: 'وسط لمتنا وعيلتي' } },
    ],
  },
  {
    id: 'first-impression',
    category: 'spicy',
    text: { en: 'What first caught your eye in me?', ar: 'فاكر أول حاجة خطفت عينك أو لفتت انتباهك فيّا؟' },
    options: [
      { id: 'looks', text: { en: 'Looks', ar: 'ستايلك وشكلك الخارجي' } },
      { id: 'humour', text: { en: 'Sense of humour', ar: 'خفة دمك وروانك' } },
      { id: 'kind', text: { en: 'Kindness', ar: 'طيبتك وطريقتك الحنينة' } },
      { id: 'energy', text: { en: 'Energy', ar: 'قايبك وطاقتك اللي في المكان' } },
      { id: 'mind', text: { en: 'The mind', ar: 'دماغك وطريقة تفكيرك' } },
    ],
  },
  {
    id: 'song-us',
    category: 'spicy',
    text: { en: 'A song that fits us?', ar: 'لو فيه أغنية بتوصف حكايتنا أو بتفكرك بينا، هتبقى إيه؟' },
    options: [
      { id: 'happy', text: { en: 'Something upbeat', ar: 'حاجة ريتمها سريع وفرحانة' } },
      { id: 'slow', text: { en: 'A slow ballad', ar: 'أغنية هادية ورومانسية أوي' } },
      { id: 'arabic', text: { en: 'An Arabic classic', ar: 'حاجة عربي كلاسيك وقديمة' } },
      { id: 'no-song', text: { en: 'No song yet', ar: 'لسه مفيش أغنية معينة حاسسها' } },
    ],
  },
  {
    id: 'morning-with-us',
    category: 'spicy',
    text: { en: 'Best morning together?', ar: 'الصباح المثالي لينا وإحنا مع بعض؟' },
    options: [
      { id: 'breakfast', text: { en: 'Big breakfast', ar: 'فطار مصري متين ولمة' } },
      { id: 'coffee', text: { en: 'Coffee in bed', ar: 'كوباية قهوة في السرير على الهادي' } },
      { id: 'walk', text: { en: 'Morning walk', ar: 'تمشية الصبح بدري والشوارع فاضية' } },
      { id: 'sleep-in', text: { en: 'Sleep in late', ar: 'نشد اللحاف وننام لحد الضهر' } },
    ],
  },
  {
    id: 'pref-music-genre',
    category: 'preferences',
    text: { en: 'Favourite music genre?', ar: 'ودنك بترتاح لـ أنهي نوع مزيكا أكتر؟' },
    options: [
      { id: 'pop', text: { en: 'Pop', ar: 'بوب خفيف وحماسي' } },
      { id: 'rock', text: { en: 'Rock', ar: 'روك وفصلان' } },
      { id: 'arabic', text: { en: 'Arabic', ar: 'عربي ومهرجانات / راب' } },
      { id: 'electronic', text: { en: 'Electronic', ar: 'إلكترونيك وتكلو' } },
      { id: 'classic', text: { en: 'Classical', ar: 'كلاسيكيات وهدوء' } },
    ],
  },
  {
    id: 'pref-coffee-style',
    category: 'preferences',
    text: { en: 'Coffee style?', ar: 'مودك في القهوة بيتشرب إزاي؟' },
    options: [
      { id: 'black', text: { en: 'Black', ar: 'سادة وسودا (بلاك)' } },
      { id: 'latte', text: { en: 'Latte', ar: 'لاتيه خفيف' } },
      { id: 'cappuccino', text: { en: 'Cappuccino', ar: 'كابتشينو برغوة' } },
      { id: 'iced', text: { en: 'Iced', ar: 'آيس كوفي تسرسوح في الحر' } },
      { id: 'turkish', text: { en: 'Turkish', ar: 'تركي بوش مظبوطة' } },
    ],
  },
  {
    id: 'pref-breakfast-style',
    category: 'preferences',
    text: { en: 'Breakfast style?', ar: 'إيه هو الفطار اللي بيفتح نفسك على اليوم؟' },
    options: [
      { id: 'heavy', text: { en: 'Heavy + savoury', ar: 'فول وطعمية ودنيا دسمة' } },
      { id: 'light', text: { en: 'Light + fresh', ar: 'خفيف.. توست وجبنة' } },
      { id: 'sweet', text: { en: 'Sweet', ar: 'حاجة مسكرة.. بان كيك أو كرواسون' } },
      { id: 'skip', text: { en: 'Skip it', ar: 'بكنسل الفطار وبدخل على الغدا' } },
    ],
  },
  {
    id: 'pref-weekend-mood',
    category: 'preferences',
    text: { en: 'Weekend mood?', ar: 'الويكند عندك بيمشي بـ أنهي طاقة؟' },
    options: [
      { id: 'active', text: { en: 'Active + out', ar: 'حركة وخروج وفرفشة' } },
      { id: 'restful', text: { en: 'Rest + nap', ar: 'سير ومخده ونوم عميق' } },
      { id: 'social', text: { en: 'Friends', ar: 'لمة صحاب وقعدة قهوة' } },
      { id: 'solo', text: { en: 'Me-time', ar: 'وقت لنفسي أفصل فيه عن الكوكب' } },
    ],
  },
  {
    id: 'pref-room-temp',
    category: 'preferences',
    text: { en: 'Room temperature?', ar: 'درجة حرارة الأوضة المثالية بالنسبالك؟' },
    options: [
      { id: 'cold', text: { en: 'Cold', ar: 'برد تلاجة' } },
      { id: 'cool', text: { en: 'Cool', ar: 'مكيفة خفيف ورايقة' } },
      { id: 'warm', text: { en: 'Warm', ar: 'دافية ومكنكنة' } },
      { id: 'hot', text: { en: 'Hot', ar: 'حر صيف مفيش تكييف' } },
    ],
  },
  {
    id: 'pref-party-type',
    category: 'preferences',
    text: { en: 'Party you\'d say yes to?', ar: 'لو جاتلك دعوة، أنهي خروجة مستحيل تقولها لأ؟' },
    options: [
      { id: 'house', text: { en: 'House gathering', ar: 'لمة هادية في البيت' } },
      { id: 'dinner', text: { en: 'Dinner party', ar: 'عزومة عشا شيك' } },
      { id: 'club', text: { en: 'Club / loud', ar: 'حفلة ومزيكا عالية وزحمة' } },
      { id: 'picnic', text: { en: 'Picnic', ar: 'قعدة برّه في الهوا وسط الخضرة' } },
    ],
  },
  {
    id: 'pref-gift-style',
    category: 'preferences',
    text: { en: 'Best kind of gift?', ar: 'إيه نوع الهدايا اللي بيبسطك من قلبك؟' },
    options: [
      { id: 'practical', text: { en: 'Practical', ar: 'حاجة عملية هستخدمها في يومي' } },
      { id: 'experience', text: { en: 'Experience', ar: 'تجربة أو خروجة نفتكرها' } },
      { id: 'luxury', text: { en: 'Luxury', ar: 'حاجة فخمة وقيمة' } },
      { id: 'handmade', text: { en: 'Handmade', ar: 'حاجة معمولة مخصوص علشاني بالإيد' } },
    ],
  },
  {
    id: 'pref-learning-style',
    category: 'preferences',
    text: { en: 'How do you learn best?', ar: 'لما بتيجي تذاكر أو تتعلم حاجة جديدة، بتستوعب إزاي؟' },
    options: [
      { id: 'read', text: { en: 'Reading', ar: 'بالقراية والتركيز في السطور' } },
      { id: 'watch', text: { en: 'Watching', ar: 'بالمشاهدة (فيديوهات وكورسات)' } },
      { id: 'listen', text: { en: 'Listening', ar: 'بالسمع (بودكاست أو حد يشرحلي)' } },
      { id: 'do', text: { en: 'Doing it', ar: 'بالتطبيق العملي والكتابة بإيدي' } },
    ],
  },
  {
    id: 'pref-dessert',
    category: 'preferences',
    text: { en: 'Favourite dessert?', ar: 'نحب نبروز التحلية بـ إيه؟' },
    options: [
      { id: 'chocolate', text: { en: 'Chocolate', ar: 'أي حاجة فيها شوكولاتة سايحة' } },
      { id: 'cake', text: { en: 'Cake', ar: 'قطعة كيك غرقانة صوص' } },
      { id: 'icecream', text: { en: 'Ice cream', ar: 'آيس كريم مرطب' } },
      { id: 'fruit', text: { en: 'Fruit', ar: 'سلطة فواكه خفيفة' } },
      { id: 'arabic', text: { en: 'Arabic sweets', ar: 'حلويات شرقي (بسبوسة وكنافة)' } },
    ],
  },
  {
    id: 'pref-cuisine',
    category: 'preferences',
    text: { en: 'Restaurant cuisine?', ar: 'لما بنقرر ناكل برّه، بتختار مطبخ أنهي بلد؟' },
    options: [
      { id: 'italian', text: { en: 'Italian', ar: 'إيطالي (باستا وبيتزا)' } },
      { id: 'lebanese', text: { en: 'Lebanese', ar: 'شامي ولبناني رايق' } },
      { id: 'japanese', text: { en: 'Japanese', ar: 'سوشي وياباني' } },
      { id: 'egyptian', text: { en: 'Egyptian', ar: 'مصري أصيل (كشري أو مشويات)' } },
      { id: 'fast', text: { en: 'Fast food', ar: 'فاست فود وسريع' } },
    ],
  },
  {
    id: 'pref-tv-type',
    category: 'preferences',
    text: { en: 'Series style?', ar: 'نوع ومود المسلسلات اللي بتسحلك؟' },
    options: [
      { id: 'drama', text: { en: 'Drama', ar: 'دراما وقصص تلمس القلب' } },
      { id: 'comedy', text: { en: 'Comedy', ar: 'كوميدي وفصلان ضحك' } },
      { id: 'thriller', text: { en: 'Thriller', ar: 'إثارة وتشويق وغموض' } },
      { id: 'docs', text: { en: 'Docs', ar: 'وثائقيات ومعلومات بجد' } },
      { id: 'reality', text: { en: 'Reality', ar: 'برامج ريالتي شو ومشاكل' } },
    ],
  },
  {
    id: 'pref-cooking-mood',
    category: 'preferences',
    text: { en: 'Cooking mood?', ar: 'الطبخ بالنسبالك إيه؟' },
    options: [
      { id: 'love', text: { en: 'Love it', ar: 'بهواه وبعتبره فن وتظبيط دماغ' } },
      { id: 'sometimes', text: { en: 'Sometimes', ar: 'ساعات لما المود يطلب' } },
      { id: 'rarely', text: { en: 'Rarely', ar: 'نادراً أوي لو عملت حاجة' } },
      { id: 'never', text: { en: 'Never', ar: 'علاقتي بالمطبخ صفر.. بطلب دليفري' } },
    ],
  },
  {
    id: 'pref-bedtime',
    category: 'preferences',
    text: { en: 'Bedtime?', ar: 'بتنام الساعة كام في الغالب؟' },
    options: [
      { id: 'before-10', text: { en: 'Before 10', ar: 'قبل 10 بالليل (نظامي جداً)' } },
      { id: '10-12', text: { en: '10 to 12', ar: 'بين 10 و 12 بالليل' } },
      { id: '12-2', text: { en: '12 to 2', ar: 'بين 12 و 2 بعد نص الليل' } },
      { id: 'after-2', text: { en: 'After 2 a.m.', ar: 'بعد 2 الفجر وسهران مع نفسي' } },
    ],
  },
  {
    id: 'pref-workout-time',
    category: 'preferences',
    text: { en: 'Workout time?', ar: 'أحلى وقت تروح فيه الجيم وتفضي طاقتك؟' },
    options: [
      { id: 'morning', text: { en: 'Morning', ar: 'الصبح بدري قبل أي حاجة' } },
      { id: 'noon', text: { en: 'Midday', ar: 'الضهر أو العصر' } },
      { id: 'evening', text: { en: 'Evening', ar: 'بالليل بعد ما أخلص اللي ورايا' } },
      { id: 'never', text: { en: 'I don\'t', ar: 'ماليش في الرياضة والحديد' } },
    ],
  },
  {
    id: 'pref-shopping',
    category: 'preferences',
    text: { en: 'Shopping style?', ar: 'طريقتك المفضلة في الشوبينج؟' },
    options: [
      { id: 'online', text: { en: 'Online', ar: 'أونلاين وأنا على السرير' } },
      { id: 'mall', text: { en: 'Mall trip', ar: 'نزلة مول وتلفيف وتجربة' } },
      { id: 'local', text: { en: 'Local shops', ar: 'المحلات القريبة السريعة' } },
      { id: 'hate', text: { en: 'I hate it', ar: 'بكره الشوبينج بكل أشكاله' } },
    ],
  },
  {
    id: 'dream-job-field',
    category: 'dreams',
    text: { en: 'Dream career field?', ar: 'مجال الشغل اللي بتحلم تكمل وتكبر فيه؟' },
    options: [
      { id: 'tech', text: { en: 'Tech', ar: 'التكنولوجيا والبرمجة والـ AI' } },
      { id: 'arts', text: { en: 'Arts', ar: 'الفنون والتصميم والإبداع' } },
      { id: 'business', text: { en: 'Business', ar: 'البيزنس والشركات وإدارة الأعمال' } },
      { id: 'medicine', text: { en: 'Medicine', ar: 'الطب والصحة' } },
      { id: 'none', text: { en: 'No work life', ar: 'مش عايز أشتغل أصلاً.. عايز أرتاح' } },
    ],
  },
  {
    id: 'dream-retirement',
    category: 'dreams',
    text: { en: 'Where do you retire?', ar: 'لما تكبر وتتقاعد، حابب تقضي بقية حياتك فين؟' },
    options: [
      { id: 'beach', text: { en: 'By the sea', ar: 'قدام البحر والروقان' } },
      { id: 'mountain', text: { en: 'In the mountains', ar: 'في الجبال وسط الطبيعة' } },
      { id: 'city', text: { en: 'In a big city', ar: 'في مدينة كبيرة وفيها حيوية' } },
      { id: 'village', text: { en: 'A village', ar: 'في قرية هادية وبعيد عن الدوشة' } },
    ],
  },
  {
    id: 'dream-big-purchase',
    category: 'dreams',
    text: { en: 'Big purchase someday?', ar: 'إيه أول حاجة تقيلة ناوي تشتريها قدام؟' },
    options: [
      { id: 'car', text: { en: 'A car', ar: 'عربية أحلامي' } },
      { id: 'house', text: { en: 'A house', ar: 'بيت ملكي باسمي' } },
      { id: 'trip', text: { en: 'A big trip', ar: 'لف العالم وسفرية براند' } },
      { id: 'save', text: { en: 'I\'d save', ar: 'مش هشتري حاجة.. هشيلهم استثمار' } },
    ],
  },
  {
    id: 'dream-language',
    category: 'dreams',
    text: { en: 'Language you\'d learn?', ar: 'لو عندك وقت تتعلم لغة جديدة تماماً، هتختار إيه؟' },
    options: [
      { id: 'french', text: { en: 'French', ar: 'فرنساوي لزوم الرقة' } },
      { id: 'spanish', text: { en: 'Spanish', ar: 'إسباني حماسي' } },
      { id: 'japanese', text: { en: 'Japanese', ar: 'ياباني واختلاف' } },
      { id: 'italian', text: { en: 'Italian', ar: 'إيطالي كله نغم' } },
      { id: 'turkish', text: { en: 'Turkish', ar: 'تركي ومسلسلات' } },
    ],
  },
  {
    id: 'dream-instrument',
    category: 'dreams',
    text: { en: 'Instrument you\'d master?', ar: 'آلة موسيقية نفسك تتقن عزفها؟' },
    options: [
      { id: 'guitar', text: { en: 'Guitar', ar: 'جيتار وروقان' } },
      { id: 'piano', text: { en: 'Piano', ar: 'بيانو كلاسيك فخم' } },
      { id: 'drums', text: { en: 'Drums', ar: 'درامز وعلو صوت' } },
      { id: 'oud', text: { en: 'Oud', ar: 'عود شرقي وسلطنة' } },
    ],
  },
  {
    id: 'dream-volunteer',
    category: 'dreams',
    text: { en: 'Cause you\'d volunteer for?', ar: 'قضية إنسانية تحب تتطوع وتساعد فيها؟' },
    options: [
      { id: 'animals', text: { en: 'Animals', ar: 'حماية وإنقاذ الحيوانات' } },
      { id: 'kids', text: { en: 'Kids', ar: 'تعليم ورعاية الأطفال' } },
      { id: 'env', text: { en: 'Environment', ar: 'الحفاظ على البيئة والطبيعة' } },
      { id: 'elderly', text: { en: 'The elderly', ar: 'مساعدة كبار السن' } },
    ],
  },
  {
    id: 'dream-skill',
    category: 'dreams',
    text: { en: 'A new skill?', ar: 'مهارة تانية بعيدة عن مجالك حابب تتعلمها؟' },
    options: [
      { id: 'cook', text: { en: 'Cooking', ar: 'فنون الطبخ الشيفات' } },
      { id: 'paint', text: { en: 'Painting', ar: 'الرسم وتفريغ الطاقة' } },
      { id: 'code', text: { en: 'Coding', ar: 'البرمجة وبناء الأفكار' } },
      { id: 'dance', text: { en: 'Dancing', ar: 'الرقص والحركة' } },
    ],
  },
  {
    id: 'dream-if-rich',
    category: 'dreams',
    text: { en: 'If suddenly rich?', ar: 'لو صحيت لقيت في حسابك ملايين فجأة، هتعمل إيه؟' },
    options: [
      { id: 'travel', text: { en: 'Travel everywhere', ar: 'أقفل اللاب توب وأسافر أفر الكوكب' } },
      { id: 'family', text: { en: 'Help family', ar: 'أظبط أهلي وعيلتي وأأمنهم' } },
      { id: 'charity', text: { en: 'Charity', ar: 'أعمال خير وصدقات تفيد الناس' } },
      { id: 'business', text: { en: 'Start a business', ar: 'أعمل بيزنس وشركات خاصة بيا' } },
    ],
  },
  {
    id: 'dream-historical',
    category: 'dreams',
    text: { en: 'Meet from history?', ar: 'لو تقدر تقابل شخصية واحدة من التاريخ، تختار مين؟' },
    options: [
      { id: 'prophet', text: { en: 'A prophet', ar: 'أحد الأنبياء والرسل الصالحين' } },
      { id: 'einstein', text: { en: 'A scientist', ar: 'عالم عبقري غير وجه البشرية' } },
      { id: 'king', text: { en: 'A king', ar: 'ملك أو قائد عظيم' } },
      { id: 'artist', text: { en: 'A great artist', ar: 'فنان أو كاتب أثر في العالم' } },
    ],
  },
  {
    id: 'dream-sport',
    category: 'dreams',
    text: { en: 'Sport you\'d master?', ar: 'رياضة نفسك تتقنها وتلعبها محترفين؟' },
    options: [
      { id: 'swim', text: { en: 'Swim', ar: 'السباحة والغوص' } },
      { id: 'tennis', text: { en: 'Tennis', ar: 'التنس لزوم التركيز' } },
      { id: 'box', text: { en: 'Boxing', ar: 'الملاكمة والفايتنج' } },
      { id: 'yoga', text: { en: 'Yoga', ar: 'اليوجا والـ الاسترتشات' } },
    ],
  },
  {
    id: 'dream-vision-10y',
    category: 'dreams',
    text: { en: '10 years from now you see…', ar: 'بعد 10 سنين من دلوقتي، شايف نفسك فين؟' },
    options: [
      { id: 'family', text: { en: 'Family-focused', ar: 'مستقر ومع عيلتي وأولادي' } },
      { id: 'career', text: { en: 'Career-focused', ar: 'مأقفل اللعبة في شغلي وبقيت بوزيشن عالي' } },
      { id: 'travel', text: { en: 'Travelling', ar: 'بلف من بلد لبلد ومش مستقر في مكان' } },
      { id: 'unsure', text: { en: 'Honestly unsure', ar: 'بصراحة مش متأكد.. بكره مخبي إيه' } },
    ],
  },
  {
    id: 'dream-success',
    category: 'dreams',
    text: { en: 'Success looks like?', ar: 'مفهوم النجاح الحقيقي بالنسبالك شكله إيه؟' },
    options: [
      { id: 'family', text: { en: 'Family + love', ar: 'بيت دافي وعيلة وحب حقيقي' } },
      { id: 'career', text: { en: 'Career impact', ar: 'بصمة وأثر كبير في مجالي' } },
      { id: 'peace', text: { en: 'Inner peace', ar: 'سلام داخلي وراحة بال بعيد عن الصياهد' } },
      { id: 'money', text: { en: 'Financial freedom', ar: 'حرية مالية ومستقبل مؤمن' } },
    ],
  },
  {
    id: 'dream-worry',
    category: 'dreams',
    text: { en: 'You worry most about?', ar: 'إيه أكتر حاجة بتفكر فيها وبتسببلك قلق للمستقبل؟' },
    options: [
      { id: 'money', text: { en: 'Money', ar: 'الفلوس وتأمين الحياة' } },
      { id: 'family', text: { en: 'Family', ar: 'مسئولية وأمان العيلة' } },
      { id: 'health', text: { en: 'Health', ar: 'الصحة والراحة الجسدية' } },
      { id: 'time', text: { en: 'Time', ar: 'الوقت اللي بيجري من غير ما أحس' } },
    ],
  },
  {
    id: 'dream-bucket',
    category: 'dreams',
    text: { en: 'Top bucket-list item?', ar: 'إيه التوب في قايمة أمنياتك اللي لازم تعملها قبل ما تموت؟' },
    options: [
      { id: 'skydiving', text: { en: 'Skydiving', ar: 'سكاي دايفينج ونط من الطيارة' } },
      { id: 'business', text: { en: 'My own business', ar: 'أبني شركتي الخاصة وتنجح' } },
      { id: 'world-travel', text: { en: 'World tour', ar: 'لف العالم كله حتة حتة' } },
      { id: 'peaceful-life', text: { en: 'Peaceful life', ar: 'أعيش في مكان هادي مفيش فيه ضغط' } },
    ],
  },
  {
    id: 'dream-companion',
    category: 'dreams',
    text: { en: 'Dream-trip companion?', ar: 'مع مين تحب تسافر رحلة العمر؟' },
    options: [
      { id: 'partner', text: { en: 'My partner', ar: 'مع شريكي وبس' } },
      { id: 'family', text: { en: 'Family', ar: 'مع كل العيلة وضحك' } },
      { id: 'friends', text: { en: 'Friends', ar: 'مع شلة الصحاب المقربين' } },
      { id: 'solo', text: { en: 'Solo', ar: 'لوحدي وأنا اللي أختار كل حاجة' } },
    ],
  },
  {
    id: 'goofy-emoji',
    category: 'goofy',
    text: { en: 'Your emoji?', ar: 'إيموجي بتحطه في كل رسالة؟' },
    options: [
      { id: 'laugh', text: { en: '😂', ar: '😂' } },
      { id: 'heart', text: { en: '❤️', ar: '❤️' } },
      { id: 'fire', text: { en: '🔥', ar: '🔥' } },
      { id: 'clown', text: { en: '🤡', ar: '🤡' } },
    ],
  },
  {
    id: 'goofy-icecream',
    category: 'goofy',
    text: { en: 'Ice cream flavour?', ar: 'لو دخلت محل آيس كريم، هتطلب طعم إيه؟' },
    options: [
      { id: 'chocolate', text: { en: 'Chocolate', ar: 'شوكولاتة كلاسيك' } },
      { id: 'vanilla', text: { en: 'Vanilla', ar: 'فانيليا وبساطة' } },
      { id: 'strawberry', text: { en: 'Strawberry', ar: 'فراولة سيتي على سيتي' } },
      { id: 'pistachio', text: { en: 'Pistachio', ar: 'فستق لذيذ ومميز' } },
      { id: 'mango', text: { en: 'Mango', ar: 'مانجا فريش بطعم مصري' } },
    ],
  },
  {
    id: 'goofy-couch',
    category: 'goofy',
    text: { en: 'Seating preference?', ar: 'بترتاح إزاي في البيت؟' },
    options: [
      { id: 'floor', text: { en: 'On the floor', ar: 'على الأرض براحتي' } },
      { id: 'sofa', text: { en: 'Sofa', ar: 'على الكنبة محضنها' } },
      { id: 'chair', text: { en: 'Chair', ar: 'على كرسي محترم' } },
      { id: 'bed', text: { en: 'In bed', ar: 'في السرير وبس' } },
    ],
  },
  {
    id: 'goofy-egyptian-breakfast',
    category: 'goofy',
    text: { en: 'Egyptian breakfast?', ar: 'فطار مصري على أصوله، تختار إيه؟' },
    options: [
      { id: 'foul', text: { en: 'Foul', ar: 'فول بالخلطة وبصل أخضر' } },
      { id: 'koshary', text: { en: 'Koshary', ar: 'كشري وصلصة على الآخر' } },
      { id: 'taameya', text: { en: 'Taameya', ar: 'طعمية مقرمشة وسخنة' } },
      { id: 'pastries', text: { en: 'Pastries', ar: 'فطائر ومخبوزات' } },
    ],
  },
  {
    id: 'goofy-drama',
    category: 'goofy',
    text: { en: 'Favourite drama?', ar: 'أنهي دراما بتشدك أكتر؟' },
    options: [
      { id: 'turkish', text: { en: 'Turkish', ar: 'تركي ورومانسية على الآخر' } },
      { id: 'korean', text: { en: 'Korean', ar: 'كوري وقصص مختلفة' } },
      { id: 'egyptian', text: { en: 'Egyptian', ar: 'مصري من الزمن الجميل' } },
      { id: 'none', text: { en: 'No drama, please', ar: 'بلاش دراما خالص' } },
    ],
  },
  {
    id: 'goofy-meme',
    category: 'goofy',
    text: { en: 'Meme vibe?', ar: 'لو هتعمل ميمز، يكون موده إيه؟' },
    options: [
      { id: 'classic', text: { en: 'Classic', ar: 'كلاسيك ومن الأقدم' } },
      { id: 'chaos', text: { en: 'Chaotic', ar: 'فوضى وعبط وضحك' } },
      { id: 'cute', text: { en: 'Cute', ar: 'كيوت ولطيف' } },
      { id: 'dark', text: { en: 'Dark', ar: 'دارك وكوميديا سودا' } },
    ],
  },
  {
    id: 'goofy-tea',
    category: 'goofy',
    text: { en: 'Your tea?', ar: 'الشاي بتشربه إزاي؟' },
    options: [
      { id: 'sugar', text: { en: 'With sugar', ar: 'بسكر زي ما اتعودنا' } },
      { id: 'mint', text: { en: 'With mint', ar: 'بنعناع وريحة جميلة' } },
      { id: 'cinnamon', text: { en: 'With cinnamon', ar: 'بقرفة دافية' } },
      { id: 'plain', text: { en: 'Plain', ar: 'سادة من غير أي حاجة' } },
    ],
  },
  {
    id: 'goofy-water',
    category: 'goofy',
    text: { en: 'Water temperature?', ar: 'الميّة بتشربها بأي درجة حرارة؟' },
    options: [
      { id: 'cold', text: { en: 'Cold', ar: 'باردة وانتعاش' } },
      { id: 'room', text: { en: 'Room temp', ar: 'عادي زي ما هي' } },
      { id: 'hot', text: { en: 'Hot', ar: 'سخنة وفي البرد بالذات' } },
      { id: 'sparkling', text: { en: 'Sparkling', ar: 'غازية بفقاقيع' } },
    ],
  },
  {
    id: 'goofy-tp',
    category: 'goofy',
    text: { en: 'Toilet paper roll?', ar: 'لفة ورق التواليت بتعلقها إزاي؟' },
    options: [
      { id: 'over', text: { en: 'Over the top', ar: 'من فوق هي الصح' } },
      { id: 'under', text: { en: 'Under the bottom', ar: 'من تحت زي الناس العاقلة' } },
      { id: 'no-care', text: { en: 'No opinion', ar: 'مش فارقة معايا أصلاً' } },
    ],
  },
  {
    id: 'goofy-nap',
    category: 'goofy',
    text: { en: 'Naps?', ar: 'نومة العصاري إيه رأيك فيها؟' },
    options: [
      { id: 'love', text: { en: 'Love them', ar: 'بحبها وبموت فيها' } },
      { id: 'hate', text: { en: 'Hate them', ar: 'بكرهها وبتفسدلي يومي' } },
      { id: 'depends', text: { en: 'Depends', ar: 'حسب اليوم والمزاج' } },
    ],
  },
  {
    id: 'goofy-shower-sing',
    category: 'goofy',
    text: { en: 'Sing in the shower?', ar: 'بتغني وانت في الحمام؟' },
    options: [
      { id: 'yes', text: { en: 'Yes, loudly', ar: 'أيوة وبصوت عالي' } },
      { id: 'sometimes', text: { en: 'Sometimes', ar: 'أحياناً وبهمهمة' } },
      { id: 'never', text: { en: 'Never', ar: 'أبداً وفي صمت تام' } },
    ],
  },
  {
    id: 'goofy-nickname',
    category: 'goofy',
    text: { en: 'Nickname for me?', ar: 'بتنادي عليّا بأي اسم دلع؟' },
    options: [
      { id: 'cute', text: { en: 'Cute one', ar: 'حاجة دلع وحلوة' } },
      { id: 'silly', text: { en: 'Silly one', ar: 'حاجة فيها هزار' } },
      { id: 'formal', text: { en: 'My full name', ar: 'بإسمي الكامل والرسمي' } },
      { id: 'none', text: { en: 'No nickname', ar: 'مفيش، باسمك العادي' } },
    ],
  },
  {
    id: 'goofy-bday-celeb',
    category: 'goofy',
    text: { en: 'Birthday vibe?', ar: 'عيد ميلادك بتحب تعمله إزاي؟' },
    options: [
      { id: 'big', text: { en: 'Big party', ar: 'حفلة كبيرة وكل الناس' } },
      { id: 'small', text: { en: 'Small + cosy', ar: 'صغيرة ومع الناس المقربة' } },
      { id: 'quiet', text: { en: 'Just family', ar: 'بس مع العيلة وعشا بسيط' } },
      { id: 'skip', text: { en: 'Skip it', ar: 'أعديه عادي من غير حاجة' } },
    ],
  },
  {
    id: 'goofy-weather-mood',
    category: 'goofy',
    text: { en: 'Weather that makes you cosy?', ar: 'أنهي جو بيخليك دافي وتحس بالراحة؟' },
    options: [
      { id: 'rain', text: { en: 'Rain', ar: 'المطر وصوته بيريحني' } },
      { id: 'snow', text: { en: 'Snow', ar: 'التلج النادر علينا' } },
      { id: 'sun', text: { en: 'Sun', ar: 'الشمس والدفا' } },
      { id: 'fall', text: { en: 'Autumn', ar: 'الخريف وأوراق الشجر' } },
    ],
  },
  {
    id: 'goofy-spice-tolerance',
    category: 'goofy',
    text: { en: 'Spice tolerance?', ar: 'بتتحمل الشطّة قد إيه؟' },
    options: [
      { id: 'none', text: { en: 'None', ar: 'خالص، بكرهها' } },
      { id: 'mild', text: { en: 'Just a hint', ar: 'لمسة بسيطة بس' } },
      { id: 'medium', text: { en: 'Medium', ar: 'متوسط ومظبوط' } },
      { id: 'fire', text: { en: 'Bring the fire', ar: 'نار وعلى الآخر' } },
    ],
  },
  {
    id: 'mem-favorite-toy',
    category: 'memories',
    text: { en: 'Favourite childhood toy?', ar: 'لعبة الطفولة اللي مش بتنساها؟' },
    options: [
      { id: 'doll', text: { en: 'Doll', ar: 'عروسة كنت بألبسها وأكلمها' } },
      { id: 'car', text: { en: 'Toy car', ar: 'عربية ريموت أو حديد' } },
      { id: 'lego', text: { en: 'Lego', ar: 'ليجو وأبني بيه دنيا' } },
      { id: 'ball', text: { en: 'Ball', ar: 'كورة وأنزل أتمتع بيها' } },
    ],
  },
  {
    id: 'mem-cartoon',
    category: 'memories',
    text: { en: 'Favourite childhood cartoon?', ar: 'كرتون الطفولة اللي كنت قاعد قدام التلفزيون عشانه؟' },
    options: [
      { id: 'tom-jerry', text: { en: 'Tom & Jerry', ar: 'توم وجيري كلاسيك' } },
      { id: 'spongebob', text: { en: 'Spongebob', ar: 'سبونج بوب والمغامرات' } },
      { id: 'pokemon', text: { en: 'Pokemon', ar: 'بوكيمون والتجميع' } },
      { id: 'arabic', text: { en: 'Arabic shows', ar: 'كرتون عربي زمان' } },
    ],
  },
  {
    id: 'mem-school-mood',
    category: 'memories',
    text: { en: 'School memory?', ar: 'ذكرياتك مع المدرسة شكلها إيه؟' },
    options: [
      { id: 'loved', text: { en: 'Loved it', ar: 'بحبها وكانت أحلى أيام' } },
      { id: 'fine', text: { en: 'It was fine', ar: 'عادية ولا فيه حاجة' } },
      { id: 'hated', text: { en: 'Hated it', ar: 'بكرهها وكانت مؤلمة' } },
      { id: 'mixed', text: { en: 'Mixed', ar: 'بين وبين، أيام وأيام' } },
    ],
  },
  {
    id: 'mem-school-subject',
    category: 'memories',
    text: { en: 'Favourite school subject?', ar: 'أكتر مادة كنت بتحبها في المدرسة؟' },
    options: [
      { id: 'math', text: { en: 'Math', ar: 'رياضيات والأرقام' } },
      { id: 'arabic', text: { en: 'Arabic', ar: 'عربي والقصص' } },
      { id: 'english', text: { en: 'English', ar: 'إنجليزي ولغة جديدة' } },
      { id: 'science', text: { en: 'Science', ar: 'علوم وتجارب' } },
      { id: 'sport', text: { en: 'PE', ar: 'رياضة وحركة' } },
    ],
  },
  {
    id: 'mem-first-game',
    category: 'memories',
    text: { en: 'First game obsession?', ar: 'أول لعبة فيديو ادمنت عليها؟' },
    options: [
      { id: 'fifa', text: { en: 'FIFA', ar: 'فيفا والكورة' } },
      { id: 'gta', text: { en: 'GTA', ar: 'جي تي إيه والمغامرات' } },
      { id: 'mobile', text: { en: 'Mobile games', ar: 'ألعاب الموبايل' } },
      { id: 'pokemon', text: { en: 'Pokemon', ar: 'بوكيمون والتجميع' } },
    ],
  },
  {
    id: 'mem-proudest',
    category: 'memories',
    text: { en: 'Proudest moment so far?', ar: 'أكتر لحظة فخر في حياتك لحد دلوقتي؟' },
    options: [
      { id: 'grad', text: { en: 'Graduation', ar: 'يوم التخرج' } },
      { id: 'work', text: { en: 'A work win', ar: 'إنجاز كبير في الشغل' } },
      { id: 'family', text: { en: 'A family moment', ar: 'لحظة مع العيلة بفخر بيها' } },
      { id: 'personal', text: { en: 'A personal goal', ar: 'تحقيق هدف شخصي' } },
    ],
  },
  {
    id: 'mem-best-vacation',
    category: 'memories',
    text: { en: 'Best vacation memory?', ar: 'أحلى ذكرى من إجازة عمرك؟' },
    options: [
      { id: 'family', text: { en: 'Family trip', ar: 'رحلة مع العيلة كاملة' } },
      { id: 'friends', text: { en: 'Friends trip', ar: 'رحلة مع الصحاب' } },
      { id: 'solo', text: { en: 'Solo trip', ar: 'سفر لوحدي وحرية' } },
      { id: 'honeymoon', text: { en: 'Honeymoon-style', ar: 'رحلة رومانسية مع شخصك' } },
    ],
  },
  {
    id: 'mem-nostalgic-song',
    category: 'memories',
    text: { en: 'Era of music you love?', ar: 'موسيقى أنهي زمن بتشدك أكتر؟' },
    options: [
      { id: '90s', text: { en: '90s', ar: 'التسعينات وأيام الكاسيت' } },
      { id: '2000s', text: { en: '2000s', ar: 'الألفينات وانطلاقة الكليبات' } },
      { id: '2010s', text: { en: '2010s', ar: 'العشرات وعصر السوشيال' } },
      { id: 'older', text: { en: 'Older', ar: 'أقدم من كده، الزمن الجميل' } },
    ],
  },
  {
    id: 'mem-school-friend',
    category: 'memories',
    text: { en: 'Best school friend now?', ar: 'صحاب المدرسة بتاعتك دلوقتي شكلهم إيه؟' },
    options: [
      { id: 'close', text: { en: 'Still close', ar: 'لسه قرايب وعلاقتي بيهم قوية' } },
      { id: 'lost', text: { en: 'Lost touch', ar: 'فقدت التواصل معاهم' } },
      { id: 'mixed', text: { en: 'Some close', ar: 'البعض لسه موجود والبعض لأ' } },
    ],
  },
  {
    id: 'mem-bday-kid',
    category: 'memories',
    text: { en: 'Best birthday gift as a kid?', ar: 'أحلى هدية عيد ميلاد كنت بتاخدها وانت صغير؟' },
    options: [
      { id: 'bike', text: { en: 'Bike', ar: 'عجلة كنت أنزل بيها الشارع' } },
      { id: 'game', text: { en: 'Game', ar: 'لعبة كنت أحلم بيها' } },
      { id: 'book', text: { en: 'Book', ar: 'كتاب يفتح عقلي' } },
      { id: 'sweets', text: { en: 'Sweets', ar: 'حلويات وشوكولاتة' } },
    ],
  },
  {
    id: 'mem-old-tv',
    category: 'memories',
    text: { en: 'TV memory you miss?', ar: 'أكتر حاجة في التلفزيون زمان وحشاك؟' },
    options: [
      { id: 'cartoons', text: { en: 'Cartoons', ar: 'الكرتون الصبح' } },
      { id: 'dramas', text: { en: 'Dramas', ar: 'المسلسلات بالليل' } },
      { id: 'sports', text: { en: 'Sports', ar: 'ماتشات الكورة' } },
      { id: 'shows', text: { en: 'Game shows', ar: 'برامج المسابقات' } },
    ],
  },
  {
    id: 'mem-first-pet',
    category: 'memories',
    text: { en: 'First pet ever?', ar: 'أول حيوان أليف كان عندك في حياتك؟' },
    options: [
      { id: 'cat', text: { en: 'Cat', ar: 'قطة كانت بتاكل معايا' } },
      { id: 'dog', text: { en: 'Dog', ar: 'كلب وفي صحبة' } },
      { id: 'fish', text: { en: 'Fish', ar: 'سمكة في حوض' } },
      { id: 'none', text: { en: 'Never had one', ar: 'مكنش عندي خالص' } },
    ],
  },
  {
    id: 'mem-nostalgic-snack',
    category: 'memories',
    text: { en: 'Kid snack you still crave?', ar: 'سناك الطفولة اللي لسه بتشتهيه دلوقتي؟' },
    options: [
      { id: 'chocolate', text: { en: 'Chocolate bars', ar: 'لوح شوكولاتة من اللي زمان' } },
      { id: 'chips', text: { en: 'Chips', ar: 'شيبسي بأنواعه القديمة' } },
      { id: 'candy', text: { en: 'Candy', ar: 'حلوى مصاصة وسكاكر' } },
      { id: 'fruit', text: { en: 'Fruit', ar: 'فاكهة الموسم' } },
    ],
  },
  {
    id: 'mem-best-eid',
    category: 'memories',
    text: { en: 'Best Eid memory?', ar: 'أحلى ذكرى عيد عشتها؟' },
    options: [
      { id: 'childhood', text: { en: 'Childhood Eids', ar: 'أعياد الطفولة بالعيدية' } },
      { id: 'recent', text: { en: 'A recent one', ar: 'عيد قريب وحلو' } },
      { id: 'always', text: { en: 'They\'re all good', ar: 'كلها حلوة من نوعها' } },
      { id: 'boring', text: { en: 'I find them dull', ar: 'بصراحة بمل منها' } },
    ],
  },
  {
    id: 'spicy-touch',
    category: 'spicy',
    text: { en: 'Favourite touch?', ar: 'اللمسة المفضلة عندك مع شريكك؟' },
    options: [
      { id: 'hug', text: { en: 'A long hug', ar: 'حضن طويل ومريح' } },
      { id: 'hand', text: { en: 'Hand-holding', ar: 'إيد في إيد ومشي' } },
      { id: 'hair', text: { en: 'Hair brush', ar: 'يلاعب شعرك ويهديك' } },
      { id: 'cheek', text: { en: 'Cheek kiss', ar: 'بوسة على الخد بتغير المود' } },
    ],
  },
  {
    id: 'spicy-evening',
    category: 'spicy',
    text: { en: 'How we end an evening?', ar: 'بتختموا السهرة مع بعض إزاي؟' },
    options: [
      { id: 'cuddle', text: { en: 'Cuddle', ar: 'حضن ودفا' } },
      { id: 'read', text: { en: 'Read together', ar: 'قراية مع بعض في صمت' } },
      { id: 'scroll', text: { en: 'Scroll silently', ar: 'كل واحد على موبايله' } },
      { id: 'talk', text: { en: 'Long talk', ar: 'كلام كتير لحد ما ننام' } },
    ],
  },
  {
    id: 'spicy-compliment',
    category: 'spicy',
    text: { en: 'Compliment you love?', ar: 'أنهي مدح بيكسبك ويفرحك؟' },
    options: [
      { id: 'cute', text: { en: 'You\'re cute', ar: 'إنت حلو' } },
      { id: 'smart', text: { en: 'You\'re smart', ar: 'إنت ذكي' } },
      { id: 'kind', text: { en: 'You\'re kind', ar: 'إنت طيب وقلبك جميل' } },
      { id: 'funny', text: { en: 'You\'re funny', ar: 'إنت دمك خفيف ومضحك' } },
    ],
  },
  {
    id: 'spicy-date-time',
    category: 'spicy',
    text: { en: 'Best time for a date?', ar: 'أحلى وقت للخروج مع شريكك؟' },
    options: [
      { id: 'morning', text: { en: 'Morning', ar: 'الصبح وفطار في حته حلوة' } },
      { id: 'afternoon', text: { en: 'Afternoon', ar: 'الظهر ونزله' } },
      { id: 'evening', text: { en: 'Evening', ar: 'العصر وقعدة' } },
      { id: 'night', text: { en: 'Night', ar: 'بالليل ورومانسية' } },
    ],
  },
  {
    id: 'spicy-date-place',
    category: 'spicy',
    text: { en: 'Date spot?', ar: 'مكان الموعد المفضل عندك؟' },
    options: [
      { id: 'home', text: { en: 'Home', ar: 'في البيت ومريح' } },
      { id: 'cafe', text: { en: 'A café', ar: 'كافيه هادي' } },
      { id: 'outside', text: { en: 'Walk outside', ar: 'مشي برّه في الشارع' } },
      { id: 'new', text: { en: 'Somewhere new', ar: 'مكان جديد ومغامرة' } },
    ],
  },
  {
    id: 'spicy-couple-trip',
    category: 'spicy',
    text: { en: 'Couple trip dream?', ar: 'حلم سفركم سوا فين؟' },
    options: [
      { id: 'paris', text: { en: 'Paris', ar: 'باريس وبرج إيفل' } },
      { id: 'istanbul', text: { en: 'Istanbul', ar: 'إسطنبول والبسفور' } },
      { id: 'dubai', text: { en: 'Dubai', ar: 'دبي والأبراج' } },
      { id: 'tokyo', text: { en: 'Tokyo', ar: 'طوكيو وثقافة مختلفة' } },
    ],
  },
  {
    id: 'spicy-bad-day-comfort',
    category: 'spicy',
    text: { en: 'After a bad day, you want?', ar: 'بعد يوم وحش، إيه اللي بيريحك من شريكك؟' },
    options: [
      { id: 'hug', text: { en: 'A long hug', ar: 'حضن طويل وهدوء' } },
      { id: 'talk', text: { en: 'To talk', ar: 'نتكلم في اللي حصل' } },
      { id: 'silence', text: { en: 'Quiet', ar: 'سكوت وأبتعد ساعة' } },
      { id: 'outing', text: { en: 'Get out', ar: 'نخرج ونغير الجو' } },
    ],
  },
  {
    id: 'spicy-best-thing-about-us',
    category: 'spicy',
    text: { en: 'Best thing about us?', ar: 'أحلى حاجة في علاقتكم مع بعض؟' },
    options: [
      { id: 'laughter', text: { en: 'How we laugh', ar: 'ضحكنا مع بعض' } },
      { id: 'understanding', text: { en: 'How we get each other', ar: 'فهمنا لبعض بدون كلام' } },
      { id: 'adventure', text: { en: 'Our adventures', ar: 'مغامراتنا سوا' } },
      { id: 'quiet', text: { en: 'The quiet moments', ar: 'لحظات الهدوء والصمت' } },
    ],
  },
  {
    id: 'spicy-anniversary',
    category: 'spicy',
    text: { en: 'Ideal anniversary?', ar: 'أحلى احتفال بذكرى السنة بتاعتكم؟' },
    options: [
      { id: 'trip', text: { en: 'A trip', ar: 'رحلة بعيد' } },
      { id: 'dinner', text: { en: 'A dinner', ar: 'عشا فاخر في مكان حلو' } },
      { id: 'surprise', text: { en: 'A surprise', ar: 'مفاجأة من اللي ما يتوقعش' } },
      { id: 'cozy', text: { en: 'A cozy night', ar: 'ليلة دافية في البيت' } },
    ],
  },
  {
    id: 'spicy-photo',
    category: 'spicy',
    text: { en: 'Favourite kind of photo of us?', ar: 'أنهي نوع صور مع بعض بتحبه أكتر؟' },
    options: [
      { id: 'silly', text: { en: 'Silly + candid', ar: 'هزار وعفوية' } },
      { id: 'cute', text: { en: 'Cute + posed', ar: 'كيوت ومنسقة' } },
      { id: 'nature', text: { en: 'Out in nature', ar: 'في الطبيعة وأماكن حلوة' } },
      { id: 'formal', text: { en: 'Formal portrait', ar: 'بورتريه رسمي' } },
    ],
  },
  {
    id: 'spicy-shared-hobby',
    category: 'spicy',
    text: { en: 'A hobby we should pick up together?', ar: 'هواية تحب تجربها مع شريكك؟' },
    options: [
      { id: 'cook', text: { en: 'Cooking', ar: 'الطبخ مع بعض' } },
      { id: 'sport', text: { en: 'A sport', ar: 'رياضة سوا' } },
      { id: 'travel', text: { en: 'Travel hunting', ar: 'ندور على رحلات' } },
      { id: 'movies', text: { en: 'Movie nights', ar: 'ليالي أفلام' } },
    ],
  },
  {
    id: 'spicy-silent-day',
    category: 'spicy',
    text: { en: 'Best silent day together?', ar: 'أحلى يوم هادي قضيته مع شريكك؟' },
    options: [
      { id: 'book', text: { en: 'Books each', ar: 'كل واحد بكتاب' } },
      { id: 'movie', text: { en: 'A long movie', ar: 'فيلم طويل وقعدة' } },
      { id: 'walk', text: { en: 'A walk', ar: 'مشي طويل في الشارع' } },
      { id: 'nap', text: { en: 'A long nap', ar: 'نوم طويل سوا' } },
    ],
  },
  {
    id: 'deep-know-love',
    category: 'deep',
    text: { en: 'How do you know when you\'re in love?', ar: 'إزاي بتعرف إنك واقع في الحب؟' },
    options: [
      { id: 'thoughts', text: { en: 'Constant thoughts of them', ar: 'بتفضل تفكر فيه طول الوقت' } },
      { id: 'peace', text: { en: 'Deep peace + safety', ar: 'بتحس براحة بال وأمان حقيقي' } },
      { id: 'sacrifice', text: { en: 'Willing to sacrifice', ar: 'بتلاقي نفسك مستعد تضحي' } },
      { id: 'butterflies', text: { en: 'Excitement + nerves', ar: 'حماس ورجفة في القلب' } },
      { id: 'just-know', text: { en: 'You just know', ar: 'بتحس وخلاص بدون شرح' } },
    ],
  },
  {
    id: 'deep-love-first-sight',
    category: 'deep',
    text: { en: 'Love at first sight — real?', ar: 'الحب من أول نظرة حقيقي ولا خيال؟' },
    options: [
      { id: 'yes', text: { en: 'Yes, completely', ar: 'أيوة فعلاً وموجود' } },
      { id: 'no', text: { en: 'No, just attraction', ar: 'لا، انجذاب بس ومش حب' } },
      { id: 'depends', text: { en: 'Maybe, depends', ar: 'يمكن، بيختلف من شخص لشخص' } },
      { id: 'movies', text: { en: 'Only in movies', ar: 'بس في الأفلام والروايات' } },
    ],
  },
  {
    id: 'deep-soulmates',
    category: 'deep',
    text: { en: 'Soulmates — do they exist?', ar: 'تفتكر توأم الروح موجود فعلاً؟' },
    options: [
      { id: 'one', text: { en: 'Yes, one person', ar: 'أيوة، شخص واحد بس في الدنيا' } },
      { id: 'many', text: { en: 'Yes, multiple', ar: 'أيوة، يمكن أكتر من واحد' } },
      { id: 'choice', text: { en: 'No — it\'s a choice', ar: 'لا، هو اختيار مش قدر' } },
      { id: 'maybe', text: { en: 'Maybe', ar: 'يمكن، مش متأكد' } },
    ],
  },
  {
    id: 'deep-marriage-view',
    category: 'deep',
    text: { en: 'Your view on marriage?', ar: 'إيه رأيك في فكرة الجواز؟' },
    options: [
      { id: 'sacred', text: { en: 'Sacred + lifelong', ar: 'مقدّس ومدى العمر' } },
      { id: 'partner', text: { en: 'A partnership', ar: 'شراكة وحياة سوا' } },
      { id: 'optional', text: { en: 'Optional', ar: 'اختياري ومش لازم' } },
      { id: 'outdated', text: { en: 'Outdated', ar: 'الزمن عدّى عليه' } },
    ],
  },
  {
    id: 'deep-divorce-why',
    category: 'deep',
    text: { en: 'Why are divorce rates so high?', ar: 'ليه نسبة الطلاق في رأيك بقت عالية؟' },
    options: [
      { id: 'give-up', text: { en: 'People give up easily', ar: 'الناس بتستسلم بسرعة' } },
      { id: 'expect', text: { en: 'Wrong expectations', ar: 'توقعات غلط من البداية' } },
      { id: 'communicate', text: { en: 'No communication', ar: 'مفيش تواصل بين الاتنين' } },
      { id: 'society', text: { en: 'Society changed', ar: 'المجتمع اتغير كتير' } },
    ],
  },
  {
    id: 'deep-people-change',
    category: 'deep',
    text: { en: 'Do people change in relationships?', ar: 'تفتكر الناس بتتغير لما تدخل علاقة؟' },
    options: [
      { id: 'better', text: { en: 'Yes, for the better', ar: 'أيوة، للأحسن غالباً' } },
      { id: 'worse', text: { en: 'Yes, for the worse', ar: 'أيوة، للأسوأ مع الوقت' } },
      { id: 'both', text: { en: 'A bit of both', ar: 'الاتنين على حسب الشخص' } },
      { id: 'no', text: { en: 'No, just adapt', ar: 'لا، بس بيتأقلموا' } },
    ],
  },
  {
    id: 'deep-compromise',
    category: 'deep',
    text: { en: 'How much should you compromise?', ar: 'تتنازل في العلاقة قد إيه؟' },
    options: [
      { id: 'lots', text: { en: 'A lot, give 70%+', ar: 'كتير، أكتر من ٧٠٪' } },
      { id: 'equal', text: { en: 'Equal — 50/50', ar: 'متساوي بالظبط ٥٠/٥٠' } },
      { id: 'small', text: { en: 'Only on small things', ar: 'بس في الحاجات الصغيرة' } },
      { id: 'never', text: { en: 'Never on core values', ar: 'مستحيل على مبادئي' } },
    ],
  },
  {
    id: 'deep-star-signs',
    category: 'deep',
    text: { en: 'Star signs + compatibility?', ar: 'تؤمن بالأبراج والتوافق بينها؟' },
    options: [
      { id: 'yes', text: { en: 'Yes, very much', ar: 'أيوة فعلاً وبشتغل بيها' } },
      { id: 'some', text: { en: 'A little', ar: 'شويّة وفي بعض الحاجات' } },
      { id: 'fun', text: { en: 'Just for fun', ar: 'هزار بس وللتسلية' } },
      { id: 'no', text: { en: 'No, not at all', ar: 'لا، خالص ومش بصدقها' } },
    ],
  },
  {
    id: 'deep-important-love',
    category: 'deep',
    text: { en: 'Most important type of love?', ar: 'أهم نوع من الحب عندك؟' },
    options: [
      { id: 'romantic', text: { en: 'Romantic', ar: 'الحب الرومانسي' } },
      { id: 'family', text: { en: 'Family', ar: 'حب العيلة والأهل' } },
      { id: 'self', text: { en: 'Self-love', ar: 'حب النفس قبل أي حد' } },
      { id: 'friends', text: { en: 'Friendship', ar: 'حب الصداقة' } },
      { id: 'divine', text: { en: 'Divine', ar: 'الحب الإلهي والروحي' } },
    ],
  },
  {
    id: 'deep-ex-friends',
    category: 'deep',
    text: { en: 'Can exes be friends?', ar: 'الإكس ينفع يبقى صاحب بعد الفراق؟' },
    options: [
      { id: 'always', text: { en: 'Yes, always', ar: 'أيوة دايماً، ليه لأ' } },
      { id: 'time', text: { en: 'After enough time', ar: 'بعد فترة من الزمن' } },
      { id: 'no-feels', text: { en: 'Only if no feelings', ar: 'بس لو مفيش مشاعر متبقية' } },
      { id: 'never', text: { en: 'No, never', ar: 'لا، أبداً' } },
    ],
  },
  {
    id: 'deep-ex-back',
    category: 'deep',
    text: { en: 'Common for exes to reunite?', ar: 'الإكس بيرجع لبعض كتير ولا لأ؟' },
    options: [
      { id: 'common', text: { en: 'Very common', ar: 'منتشر جداً' } },
      { id: 'some', text: { en: 'Sometimes', ar: 'أحياناً وحسب الموقف' } },
      { id: 'rare', text: { en: 'Rarely works', ar: 'نادراً ما بينجح' } },
      { id: 'never', text: { en: 'Never works', ar: 'مش بينجح أبداً' } },
    ],
  },
  {
    id: 'deep-best-relationship',
    category: 'deep',
    text: { en: 'Best thing about being together?', ar: 'أحلى حاجة في إنك في علاقة جدية؟' },
    options: [
      { id: 'partner', text: { en: 'Having a partner', ar: 'إن فيه شريك جنبك في الحياة' } },
      { id: 'small', text: { en: 'Sharing small moments', ar: 'مشاركة اللحظات الصغيرة' } },
      { id: 'chosen', text: { en: 'Feeling chosen', ar: 'حسك إنك مختار ومحبوب' } },
      { id: 'future', text: { en: 'Building a future', ar: 'بناء مستقبل سوا' } },
    ],
  },
  {
    id: 'deep-best-advice',
    category: 'deep',
    text: { en: 'Best relationship advice?', ar: 'أحسن نصيحة سمعتها في العلاقات؟' },
    options: [
      { id: 'angry', text: { en: 'Never sleep angry', ar: 'ما تناموش وانتو زعلانين' } },
      { id: 'talk', text: { en: 'Talk about everything', ar: 'اتكلموا في كل حاجة' } },
      { id: 'battles', text: { en: 'Pick your battles', ar: 'مش كل حاجة تستاهل خناقة' } },
      { id: 'date', text: { en: 'Keep dating each other', ar: 'فضلوا تطلعوا مع بعض' } },
    ],
  },
  {
    id: 'deep-scares-love',
    category: 'deep',
    text: { en: 'One thing that scares you about love?', ar: 'أكتر حاجة بتخوّفك من الحب؟' },
    options: [
      { id: 'self', text: { en: 'Losing myself', ar: 'إني أضيع نفسي وأنسى ذاتي' } },
      { id: 'hurt', text: { en: 'Getting hurt', ar: 'إني أتجرح وأتأذى' } },
      { id: 'leave', text: { en: 'Being abandoned', ar: 'إنه يسيبني وأبقى لوحدي' } },
      { id: 'trapped', text: { en: 'Being trapped', ar: 'إني أتحبس ومفيش مخرج' } },
      { id: 'nothing', text: { en: 'Nothing scares me', ar: 'مفيش حاجة بتخوّفني خالص' } },
    ],
  },
  {
    id: 'deep-fall-easy',
    category: 'deep',
    text: { en: 'How easy is it to fall in love?', ar: 'الوقوع في الحب بالنسبالك سهل قد إيه؟' },
    options: [
      { id: 'very-easy', text: { en: 'Very easy', ar: 'سهل قوي وبيحصل لي كتير' } },
      { id: 'right-time', text: { en: 'Easy when right', ar: 'سهل مع الشخص الصح' } },
      { id: 'hard', text: { en: 'Hard, takes time', ar: 'صعب وبياخد وقت طويل' } },
      { id: 'very-hard', text: { en: 'Very hard', ar: 'صعب قوي ومش بحب أصلاً' } },
    ],
  },
  {
    id: 'deep-fall-out',
    category: 'deep',
    text: { en: 'Why do people fall out of love?', ar: 'ليه الناس بيخلص حبها لبعض؟' },
    options: [
      { id: 'apart', text: { en: 'Grow apart', ar: 'بيبعدوا عن بعض شوية شوية' } },
      { id: 'routine', text: { en: 'Routine kills it', ar: 'الروتين بيقتل الحب' } },
      { id: 'not-love', text: { en: 'Wasn\'t love', ar: 'مكنش حب أصلاً من البداية' } },
      { id: 'effort', text: { en: 'Effort fades', ar: 'المجهود من الجنبين بيقل' } },
    ],
  },
  {
    id: 'deep-think-love',
    category: 'deep',
    text: { en: 'People THINK they\'re in love but aren\'t?', ar: 'الناس بتفتكر إنها بتحب وهي مش فعلاً كده؟' },
    options: [
      { id: 'common', text: { en: 'Very common', ar: 'منتشر جداً وبيحصل كتير' } },
      { id: 'some', text: { en: 'Sometimes', ar: 'أحياناً وفي مواقف معينة' } },
      { id: 'rare', text: { en: 'Rare', ar: 'نادراً ما بيحصل' } },
      { id: 'no-tell', text: { en: 'Can\'t tell', ar: 'مش هتعرف الفرق أصلاً' } },
    ],
  },
  {
    id: 'deep-trust-hard',
    category: 'deep',
    text: { en: 'Hard to trust in love?', ar: 'الثقة في الحب صعبة بالنسبالك؟' },
    options: [
      { id: 'yes-hard', text: { en: 'Yes, very hard', ar: 'صعبة قوي ومش سهلة' } },
      { id: 'worth', text: { en: 'Hard but worth it', ar: 'صعبة بس بتستاهل المجهود' } },
      { id: 'no-easy', text: { en: 'No, not for me', ar: 'لا، مش بالنسبالي' } },
      { id: 'past', text: { en: 'Depends on past', ar: 'حسب التجارب الماضية' } },
    ],
  },
  {
    id: 'deep-trust-meaning',
    category: 'deep',
    text: { en: 'Fully trusting someone means?', ar: 'الثقة الكاملة في حد معناها إيه؟' },
    options: [
      { id: 'believe', text: { en: 'Believing everything', ar: 'إنك تصدق كل كلامه' } },
      { id: 'phone', text: { en: 'Open access to phone', ar: 'إن الموبايل مفتوح بينكوا' } },
      { id: 'intent', text: { en: 'Trusting intentions', ar: 'إنك تثق في نواياه دايماً' } },
      { id: 'peace', text: { en: 'Peace + no doubts', ar: 'راحة من غير شك ولا قلق' } },
    ],
  },
  {
    id: 'deep-trust-rebuild',
    category: 'deep',
    text: { en: 'Can broken trust be rebuilt?', ar: 'الثقة المكسورة ممكن ترجع تاني؟' },
    options: [
      { id: 'yes-work', text: { en: 'Yes, with work', ar: 'أيوة بمجهود من الاتنين' } },
      { id: 'maybe', text: { en: 'Maybe, depends', ar: 'يمكن، بيختلف على حسب الموقف' } },
      { id: 'not-same', text: { en: 'Never the same', ar: 'مش هترجع زي الأول أبداً' } },
      { id: 'no', text: { en: 'No, never', ar: 'لا، أبداً ومستحيل' } },
    ],
  },
  {
    id: 'deep-cheating-def',
    category: 'deep',
    text: { en: 'What counts as cheating?', ar: 'إيه اللي بتعتبره خيانة في رأيك؟' },
    options: [
      { id: 'physical', text: { en: 'Physical only', ar: 'الجسدية بس' } },
      { id: 'emotion', text: { en: 'Emotional only', ar: 'العاطفية بس' } },
      { id: 'both', text: { en: 'Both equally', ar: 'الاتنين سوا زي بعض' } },
      { id: 'flirt', text: { en: 'Even flirty texts', ar: 'حتى الرسايل اللعوبة كمان' } },
    ],
  },
  {
    id: 'deep-affair-worse',
    category: 'deep',
    text: { en: 'Emotional or physical affair — worse?', ar: 'الخيانة العاطفية ولا الجسدية أوحش عندك؟' },
    options: [
      { id: 'emotion', text: { en: 'Emotional', ar: 'العاطفية أصعب وأوجع' } },
      { id: 'physical', text: { en: 'Physical', ar: 'الجسدية أوحش' } },
      { id: 'both', text: { en: 'Both equal', ar: 'الاتنين زي بعض في الألم' } },
      { id: 'context', text: { en: 'Depends', ar: 'حسب الموقف والسياق' } },
    ],
  },
  {
    id: 'deep-karma',
    category: 'deep',
    text: { en: 'Do you believe in karma?', ar: 'تؤمن بالكارما والجزاء من جنس العمل؟' },
    options: [
      { id: 'yes', text: { en: 'Yes, always', ar: 'أيوة دايماً وموجودة' } },
      { id: 'some', text: { en: 'Sometimes', ar: 'أحياناً وفي بعض الأوقات' } },
      { id: 'coinc', text: { en: 'Just coincidence', ar: 'مجرد صدفة بس' } },
      { id: 'no', text: { en: 'No', ar: 'لا، مش بصدقها' } },
    ],
  },
  {
    id: 'deep-others-opinion',
    category: 'deep',
    text: { en: 'Weight of others\' opinions on us?', ar: 'رأي الناس فيكم بيفرق معاكوا قد إيه؟' },
    options: [
      { id: 'lot', text: { en: 'A lot — they see', ar: 'كتير، الناس بيشوفوا الصورة كاملة' } },
      { id: 'little', text: { en: 'A little, advice', ar: 'شويّة وكمصدر للمشورة' } },
      { id: 'none', text: { en: 'Almost none', ar: 'تقريباً ولا حاجة' } },
      { id: 'zero', text: { en: 'Zero', ar: 'صفر، رأيهم ميهمنيش' } },
    ],
  },
  {
    id: 'deep-family-involve',
    category: 'deep',
    text: { en: 'Family involvement in a relationship?', ar: 'العيلة لازم تدخل قد إيه في علاقتكم؟' },
    options: [
      { id: 'lots', text: { en: 'Very involved', ar: 'كتير، رأيهم مهم' } },
      { id: 'distance', text: { en: 'Support from afar', ar: 'دعم من بعيد بس' } },
      { id: 'big', text: { en: 'Only big decisions', ar: 'في القرارات الكبيرة بس' } },
      { id: 'out', text: { en: 'Stay out', ar: 'بعيد خالص، علاقتنا بس' } },
    ],
  },
  {
    id: 'deep-unhealthy-signs',
    category: 'deep',
    text: { en: 'Top sign of an unhealthy relationship?', ar: 'أكبر علامة على إن العلاقة مش صحية؟' },
    options: [
      { id: 'fights', text: { en: 'Constant fighting', ar: 'خناق دايم بدون توقف' } },
      { id: 'no-fun', text: { en: 'No more laughter', ar: 'الضحك بينكوا خلص' } },
      { id: 'eggs', text: { en: 'Walking on eggshells', ar: 'بتحسب كلامك معاه طول الوقت' } },
      { id: 'alone', text: { en: 'Alone together', ar: 'وحيدين برغم إنكوا مع بعض' } },
      { id: 'lies', text: { en: 'Lying', ar: 'كذب وعدم صراحة' } },
    ],
  },
  {
    id: 'deep-healthy-makes',
    category: 'deep',
    text: { en: 'What makes love last?', ar: 'إيه أكتر حاجة بتخلي الحب يدوم؟' },
    options: [
      { id: 'comm', text: { en: 'Communication', ar: 'التواصل المستمر' } },
      { id: 'values', text: { en: 'Shared values', ar: 'القيم المشتركة' } },
      { id: 'effort', text: { en: 'Daily effort', ar: 'مجهود يومي من الاتنين' } },
      { id: 'phys', text: { en: 'Physical closeness', ar: 'القرب الجسدي' } },
      { id: 'all', text: { en: 'All of them', ar: 'كل ده مع بعض' } },
    ],
  },
  {
    id: 'deep-norm-gestures',
    category: 'deep',
    text: { en: 'Romantic gesture that should be the norm?', ar: 'لفتة رومانسية مفروض تبقى عادية ويومية؟' },
    options: [
      { id: 'texts', text: { en: 'Daily texts', ar: 'رسايل كل يوم' } },
      { id: 'gifts', text: { en: 'Small surprises', ar: 'مفاجآت صغيرة بسيطة' } },
      { id: 'dates', text: { en: 'Date nights', ar: 'مواعيد منتظمة' } },
      { id: 'iloveu', text: { en: '"I love you"', ar: 'كلمة بحبّك بدون مناسبة' } },
      { id: 'touch', text: { en: 'Daily touch', ar: 'لمسة دافية يومياً' } },
    ],
  },
  {
    id: 'deep-connect-type',
    category: 'deep',
    text: { en: 'Strongest connection — physical or emotional?', ar: 'أقوى تواصل في العلاقة جسدي ولا عاطفي؟' },
    options: [
      { id: 'physical', text: { en: 'Physical', ar: 'جسدي وأهم في الحب' } },
      { id: 'emotion', text: { en: 'Emotional', ar: 'عاطفي وروحي الأهم' } },
      { id: 'both', text: { en: 'Both equally', ar: 'الاتنين سوا متوازنين' } },
      { id: 'spirit', text: { en: 'Spiritual above all', ar: 'روحي فوق الاتنين' } },
    ],
  },
  {
    id: 'deep-most-problems',
    category: 'deep',
    text: { en: 'Top cause of relationship problems?', ar: 'أكتر سبب لمشاكل العلاقات في رأيك؟' },
    options: [
      { id: 'money', text: { en: 'Money', ar: 'الفلوس والأمور المادية' } },
      { id: 'comm', text: { en: 'Communication', ar: 'عدم التواصل' } },
      { id: 'fam', text: { en: 'In-laws', ar: 'الأهل والعيلة' } },
      { id: 'trust', text: { en: 'Trust', ar: 'الثقة المكسورة' } },
      { id: 'out', text: { en: 'Outside opinions', ar: 'آراء الناس من برّه' } },
    ],
  },
  {
    id: 'deep-income-gap',
    category: 'deep',
    text: { en: 'One earning more — matters?', ar: 'لو واحد فيكم بيكسب أكتر، بيفرق في العلاقة؟' },
    options: [
      { id: 'lots', text: { en: 'Yes, a lot', ar: 'أيوة كتير وبيأثر' } },
      { id: 'some', text: { en: 'A little', ar: 'شويّة في بعض المواقف' } },
      { id: 'no', text: { en: 'Not really', ar: 'مش بالكتير' } },
      { id: 'zero', text: { en: 'Doesn\'t matter at all', ar: 'مفرقش خالص' } },
    ],
  },
  {
    id: 'deep-finding-hard',
    category: 'deep',
    text: { en: 'Hardest about finding "the one"?', ar: 'أصعب حاجة في إنك تلاقي الشخص الصح؟' },
    options: [
      { id: 'timing', text: { en: 'Right timing', ar: 'التوقيت المناسب' } },
      { id: 'compat', text: { en: 'Compatibility', ar: 'التوافق والكيمياء' } },
      { id: 'trust', text: { en: 'Trust', ar: 'الثقة في الناس' } },
      { id: 'sure', text: { en: 'Being sure', ar: 'إنك تتأكد إنه هو فعلاً' } },
    ],
  },
  {
    id: 'deep-dating-today',
    category: 'deep',
    text: { en: 'Dating today is…', ar: 'الدنيا دلوقتي في العلاقات شكلها إيه؟' },
    options: [
      { id: 'easier', text: { en: 'Easier than before', ar: 'أسهل من زمان' } },
      { id: 'harder', text: { en: 'Harder than before', ar: 'أصعب من زمان' } },
      { id: 'same', text: { en: 'Just different', ar: 'مختلفة بس مش أصعب' } },
      { id: 'confuse', text: { en: 'Confusing', ar: 'مربكة ومش واضحة' } },
    ],
  },
  {
    id: 'deep-extra-vs-ord',
    category: 'deep',
    text: { en: 'Extraordinary love that ends, or ordinary that stays?', ar: 'حب استثنائي وقصير، ولا حب عادي وبيدوم؟' },
    options: [
      { id: 'extra', text: { en: 'Extraordinary, short', ar: 'استثنائي حتى لو قصير' } },
      { id: 'ord', text: { en: 'Ordinary, lasting', ar: 'عادي بس بيدوم العمر' } },
      { id: 'mix', text: { en: 'A bit of both', ar: 'حاجة في النص' } },
      { id: 'both', text: { en: 'I want both', ar: 'عاوز الاتنين سوا' } },
    ],
  },
  {
    id: 'deep-dramatic',
    category: 'deep',
    text: { en: 'Do something dramatic for love?', ar: 'ممكن تعمل حاجة جنونية علشان الحب؟' },
    options: [
      { id: 'heart', text: { en: 'In a heartbeat', ar: 'فوراً وبدون تفكير' } },
      { id: 'right', text: { en: 'Only for the one', ar: 'بس للشخص الصح' } },
      { id: 'reason', text: { en: 'Within reason', ar: 'في حدود المعقول' } },
      { id: 'no', text: { en: 'No, never', ar: 'لا، أبداً' } },
    ],
  },
  {
    id: 'deep-scared-relationships',
    category: 'deep',
    text: { en: 'What people fear most in love?', ar: 'أكتر حاجة بتخوف الناس في الحب؟' },
    options: [
      { id: 'hurt', text: { en: 'Getting hurt', ar: 'إنهم يتجرحوا' } },
      { id: 'judge', text: { en: 'Being judged', ar: 'الناس تحكم عليهم' } },
      { id: 'self', text: { en: 'Losing themselves', ar: 'إنهم يضيعوا نفسهم' } },
      { id: 'commit', text: { en: 'Commitment', ar: 'الالتزام والمسؤولية' } },
    ],
  },
  {
    id: 'deep-social-impact',
    category: 'deep',
    text: { en: 'Social media + relationships?', ar: 'السوشيال ميديا تأثيرها على العلاقات إيه؟' },
    options: [
      { id: 'neg', text: { en: 'Mostly negative', ar: 'سلبي بشكل عام' } },
      { id: 'pos', text: { en: 'Mostly positive', ar: 'إيجابي بشكل عام' } },
      { id: 'both', text: { en: 'Both equally', ar: 'الاتنين زي بعض' } },
      { id: 'none', text: { en: 'No real impact', ar: 'مفيش تأثير حقيقي' } },
    ],
  },
  {
    id: 'deep-school-love',
    category: 'deep',
    text: { en: 'Should love be taught in schools?', ar: 'الحب لازم يتدرس في المدارس؟' },
    options: [
      { id: 'vital', text: { en: 'Yes — vital skill', ar: 'أيوة، مهارة أساسية في الحياة' } },
      { id: 'basic', text: { en: 'Yes — the basics', ar: 'أيوة، الأساسيات بس' } },
      { id: 'values', text: { en: 'Just values', ar: 'القيم بس، مش الحب' } },
      { id: 'no', text: { en: 'No — too personal', ar: 'لا، حاجة شخصية مش بتتدرس' } },
    ],
  },
  {
    id: 'deep-younger-self',
    category: 'deep',
    text: { en: 'Top lesson for younger self?', ar: 'أهم درس عاوز تقوله لنفسك زمان؟' },
    options: [
      { id: 'work', text: { en: 'It takes work', ar: 'إن الحب بياخد مجهود' } },
      { id: 'pick', text: { en: 'Pick wisely', ar: 'اختار شريكك بعقل' } },
      { id: 'self', text: { en: 'Be yourself', ar: 'كون نفسك مش حد تاني' } },
      { id: 'slow', text: { en: 'Don\'t rush', ar: 'متستعجلش في حاجة' } },
    ],
  },
  {
    id: 'deep-romance-importance',
    category: 'deep',
    text: { en: 'How important is romantic love?', ar: 'الحب الرومانسي قد إيه مهم في حياتك؟' },
    options: [
      { id: 'most', text: { en: 'Most important thing', ar: 'أهم حاجة في الدنيا' } },
      { id: 'very', text: { en: 'Very important', ar: 'مهم جداً جداً' } },
      { id: 'one', text: { en: 'One of many', ar: 'واحد من حاجات كتير مهمة' } },
      { id: 'less', text: { en: 'Not that important', ar: 'مش بالأهمية دي عندي' } },
    ],
  },
  {
    id: 'deep-beautiful-love',
    category: 'deep',
    text: { en: 'Most beautiful thing about love?', ar: 'أحلى حاجة في الحب عندك؟' },
    options: [
      { id: 'known', text: { en: 'Being known', ar: 'إنك تتفهم بدون كلام' } },
      { id: 'build', text: { en: 'Building together', ar: 'بناء حياة سوا' } },
      { id: 'small', text: { en: 'Small daily moments', ar: 'اللحظات الصغيرة اليومية' } },
      { id: 'chose', text: { en: 'Feeling chosen', ar: 'حسك إنك مختار' } },
    ],
  },
  {
    id: 'deep-hard-in-love',
    category: 'deep',
    text: { en: 'Hardest about being in love?', ar: 'أصعب حاجة في إنك بتحب فعلاً؟' },
    options: [
      { id: 'vuln', text: { en: 'Vulnerability', ar: 'إن مشاعرك تبان وانت ضعيف' } },
      { id: 'sacrif', text: { en: 'Sacrifice', ar: 'التضحية بحاجات مهمة' } },
      { id: 'fear', text: { en: 'Fear of loss', ar: 'الخوف من الفقد' } },
      { id: 'compromise', text: { en: 'Compromise', ar: 'التنازل في كل حاجة' } },
    ],
  },
  {
    id: 'deep-mistreat-love',
    category: 'deep',
    text: { en: 'Why do people mistreat those they love?', ar: 'ليه الناس بتجرح اللي بتحبه؟' },
    options: [
      { id: 'often', text: { en: 'Happens often', ar: 'بيحصل كتير لأسباب كتيرة' } },
      { id: 'some', text: { en: 'Sometimes', ar: 'أحياناً وعن غير قصد' } },
      { id: 'rare', text: { en: 'Rarely', ar: 'نادراً ما يحصل' } },
      { id: 'not-love', text: { en: 'Then it\'s not love', ar: 'يبقى مش حب أصلاً' } },
    ],
  },
  {
    id: 'deep-self-love-first',
    category: 'deep',
    text: { en: 'Self-love before loving another?', ar: 'لازم تحب نفسك قبل ما تحب حد تاني؟' },
    options: [
      { id: 'yes', text: { en: 'Absolutely', ar: 'قطعاً ومن غير نقاش' } },
      { id: 'most', text: { en: 'Mostly yes', ar: 'غالباً أيوة' } },
      { id: 'grow', text: { en: 'Both can grow together', ar: 'الاتنين بيكبروا سوا' } },
      { id: 'no', text: { en: 'Not necessary', ar: 'مش لازم بالضرورة' } },
    ],
  },
  {
    id: 'deep-show-self',
    category: 'deep',
    text: { en: 'Best way to love yourself?', ar: 'أحسن طريقة تحب بيها نفسك؟' },
    options: [
      { id: 'care', text: { en: 'Daily self-care', ar: 'اعتناء بنفسك كل يوم' } },
      { id: 'bound', text: { en: 'Strong boundaries', ar: 'حدود قوية وواضحة' } },
      { id: 'forgive', text: { en: 'Self-forgiveness', ar: 'تسامح مع نفسك' } },
      { id: 'alone', text: { en: 'Time alone', ar: 'وقت لنفسك لوحدك' } },
    ],
  },
  {
    id: 'deep-family-shaped',
    category: 'deep',
    text: { en: 'Your family\'s impact on how you love?', ar: 'تأثير عيلتك على طريقة حبك للناس؟' },
    options: [
      { id: 'good', text: { en: 'Showed me what to do', ar: 'وروني الصح في كل حاجة' } },
      { id: 'bad', text: { en: 'Showed me what NOT to do', ar: 'وروني الغلط واتعلمت منهم' } },
      { id: 'mixed', text: { en: 'Mixed lessons', ar: 'دروس مختلطة كويس وغلط' } },
      { id: 'indep', text: { en: 'Independent of them', ar: 'بعيد عنهم خالص' } },
    ],
  },
  {
    id: 'deep-tv-love',
    category: 'deep',
    text: { en: 'TV/movies + perception of love?', ar: 'الأفلام والمسلسلات تأثيرها على نظرتك للحب؟' },
    options: [
      { id: 'neg', text: { en: 'False expectations', ar: 'بتعملي توقعات وهمية' } },
      { id: 'pos', text: { en: 'Inspires us', ar: 'بتلهمني وبتفرحني' } },
      { id: 'both', text: { en: 'Both equally', ar: 'الاتنين سوا' } },
      { id: 'none', text: { en: 'No real impact', ar: 'مفيش تأثير حقيقي' } },
    ],
  },
  {
    id: 'deep-relationships-change',
    category: 'deep',
    text: { en: 'How have relationships changed?', ar: 'العلاقات اتغيرت من زمان لدلوقتي إزاي؟' },
    options: [
      { id: 'free', text: { en: 'More freedom', ar: 'بقى فيها حرية أكتر' } },
      { id: 'less', text: { en: 'Less commitment', ar: 'بقى فيها التزام أقل' } },
      { id: 'open', text: { en: 'More open', ar: 'بقت أكتر صراحة' } },
      { id: 'cold', text: { en: 'Less personal', ar: 'بقت أقل دفء' } },
    ],
  },
  {
    id: 'deep-era',
    category: 'deep',
    text: { en: 'An era you\'d visit for love?', ar: 'لو هترجع بالزمن عشان تعيش قصة حب، تختار أنهي زمن؟' },
    options: [
      { id: '50s', text: { en: 'Romantic 50s', ar: 'الخمسينات والزمن الجميل الشيك' } },
      { id: '60s', text: { en: 'Free 60s / 70s', ar: 'الستينات والسبعينات والروقان' } },
      { id: 'old', text: { en: 'The 1800s', ar: 'القرن التسعتاشر والجوابات والفساتين' } },
      { id: 'now', text: { en: 'Stay in today', ar: 'أفضل في زماني وعصر التكنولوجيا' } },
    ],
  },
  {
    id: 'deep-cultures',
    category: 'deep',
    text: { en: 'Do relationships vary by culture?', ar: 'تفتكر لغة العلاقات وشكلها بيختلف حسب ثقافة البلد؟' },
    options: [
      { id: 'lot', text: { en: 'A lot', ar: 'كتير طبعاً.. الثقافة بتشكل كل حاجة' } },
      { id: 'some', text: { en: 'A bit', ar: 'شوية تفاصيل بس الأصل واحد' } },
      { id: 'surface', text: { en: 'Only on the surface', ar: 'بس على السطح' } },
      { id: 'same', text: { en: 'Same everywhere', ar: 'زي بعضها' } },
    ],
  },

  // ─── intimate · the 36 questions to fall in love ───────────────
  // Adapted from Arthur Aron's classic 36-question study. Each open-
  // ended prompt is reshaped into a 3-5 multi-choice so it fits the
  // quiz format; "tell your partner" style questions become "which
  // of these is most..." picks so both sides still have something
  // to guess at.
  {
    id: '36q-dinner-guest', category: 'intimate',
    text: { en: 'Anyone in the world for dinner — who?', ar: 'لو تقدر تتعشّى مع أي حد في الدنيا، تختار مين؟' },
    options: [
      { id: 'historical', text: { en: 'A figure from history', ar: 'شخصية من التاريخ' } },
      { id: 'celeb', text: { en: 'A current celebrity', ar: 'نجم مشهور دلوقتي' } },
      { id: 'lost', text: { en: 'Someone I loved who passed', ar: 'حد كنت بحبه وراح' } },
      { id: 'religious', text: { en: 'A prophet or religious figure', ar: 'نبي أو شخصية دينية' } },
    ],
  },
  {
    id: '36q-famous', category: 'intimate',
    text: { en: 'Want to be famous? In what way?', ar: 'نفسك تكون مشهور؟ في إيه؟' },
    options: [
      { id: 'impact', text: { en: 'Yes, to leave a mark', ar: 'أيوة، علشان أأثّر في الناس' } },
      { id: 'field', text: { en: 'Yes, only in my field', ar: 'أيوة، بس في مجالي' } },
      { id: 'privacy', text: { en: 'No, I value privacy', ar: 'لا، بحب الخصوصية' } },
      { id: 'maybe', text: { en: "Depends — it's complicated", ar: 'يمكن، حسب الموقف' } },
    ],
  },
  {
    id: '36q-rehearse-calls', category: 'intimate',
    text: { en: 'Do you rehearse phone calls before making them?', ar: 'بتجهّز الكلام قبل ما تتصل بحد؟' },
    options: [
      { id: 'always', text: { en: 'Yes, always', ar: 'أيوة، دايماً' } },
      { id: 'important', text: { en: 'Only for important ones', ar: 'بس في المكالمات المهمة' } },
      { id: 'sometimes', text: { en: 'Sometimes', ar: 'أحياناً' } },
      { id: 'never', text: { en: 'Never — I wing it', ar: 'لا، بمشي على اللي يجي' } },
    ],
  },
  {
    id: '36q-perfect-day', category: 'intimate',
    text: { en: "What does a perfect day look like for you?", ar: 'اليوم المثالي شكله إيه عندك؟' },
    options: [
      { id: 'home', text: { en: 'Quiet at home', ar: 'في البيت ومحدش يزعجني' } },
      { id: 'outdoor', text: { en: 'Out in nature', ar: 'في الطبيعة ومغامرة' } },
      { id: 'people', text: { en: 'With family and friends', ar: 'مع العيلة والصحاب' } },
      { id: 'passion', text: { en: 'Doing what I love', ar: 'بشتغل في حاجة بحبها' } },
      { id: 'mix', text: { en: 'A bit of everything', ar: 'خليط من كله' } },
    ],
  },
  {
    id: '36q-last-sang', category: 'intimate',
    text: { en: 'When did you last sing to yourself?', ar: 'آخر مرة غنّيت لنفسك إمتى؟' },
    options: [
      { id: 'today', text: { en: 'Today', ar: 'النهارده' } },
      { id: 'recent', text: { en: 'These last days', ar: 'في الأيام دي' } },
      { id: 'ago', text: { en: 'A long while ago', ar: 'من فترة طويلة' } },
      { id: 'never', text: { en: "I don't really sing", ar: 'مش بغني أصلاً' } },
    ],
  },
  {
    id: '36q-mind-or-body', category: 'intimate',
    text: { en: 'Live to 90 — keep mind or body of a 30-year-old?', ar: 'لو هتعيش لـ٩٠ سنة، تختار تحتفظ بعقل ولا جسم شاب؟' },
    options: [
      { id: 'mind', text: { en: 'Mind — clarity matters more', ar: 'العقل، الوعي أهم' } },
      { id: 'body', text: { en: 'Body — health and motion', ar: 'الجسم، الصحة والحركة' } },
      { id: 'hard', text: { en: "Honestly can't pick", ar: 'صعب أختار' } },
    ],
  },
  {
    id: '36q-death-hunch', category: 'intimate',
    text: { en: 'Do you have a hunch about how you will die?', ar: 'عندك إحساس داخلي إنك هتموت إزاي؟' },
    options: [
      { id: 'illness', text: { en: 'Yes — illness', ar: 'أيوة، مرض' } },
      { id: 'accident', text: { en: 'Yes — accident', ar: 'أيوة، حادثة' } },
      { id: 'old-age', text: { en: 'Yes — peacefully, old', ar: 'أيوة، موت طبيعي بعد عمر طويل' } },
      { id: 'no-think', text: { en: "I don't think about it", ar: 'ما بفكّرش في الموضوع' } },
    ],
  },
  {
    id: '36q-in-common', category: 'intimate',
    text: { en: 'Strongest thing you and your partner share?', ar: 'أكتر حاجة مشتركة بينك وبين شريكك؟' },
    options: [
      { id: 'values', text: { en: 'Core values', ar: 'القيم والمبادئ' } },
      { id: 'humour', text: { en: 'Sense of humour', ar: 'الضحك ونفس روح الفكاهة' } },
      { id: 'ambition', text: { en: 'Ambition and drive', ar: 'الطموح والشغف' } },
      { id: 'taste', text: { en: 'Taste in everything', ar: 'الذوق في كل حاجة' } },
      { id: 'past', text: { en: 'Shared life experiences', ar: 'تجارب الحياة المشتركة' } },
    ],
  },
  {
    id: '36q-grateful', category: 'intimate',
    text: { en: 'What are you most grateful for?', ar: 'أكتر حاجة بتحس بامتنان ليها؟' },
    options: [
      { id: 'health', text: { en: 'My health', ar: 'الصحة' } },
      { id: 'family', text: { en: 'My family', ar: 'العيلة' } },
      { id: 'love', text: { en: 'The people I love', ar: 'اللي بحبهم' } },
      { id: 'growth', text: { en: 'How much I have grown', ar: 'النضوج اللي حصل فيا' } },
      { id: 'chances', text: { en: 'The chances I have had', ar: 'الفرص اللي اتدت لي' } },
    ],
  },
  {
    id: '36q-change-raised', category: 'intimate',
    text: { en: 'Change one thing about how you were raised?', ar: 'لو تقدر تغيّر حاجة في طريقة تربيتك، تغيّر إيه؟' },
    options: [
      { id: 'emotion', text: { en: 'More emotional expression', ar: 'مشاعر أكتر وتعبير عن الحب' } },
      { id: 'independence', text: { en: 'More independence', ar: 'استقلالية أكتر' } },
      { id: 'support', text: { en: 'More support for my choices', ar: 'دعم في اختياراتي' } },
      { id: 'pressure', text: { en: 'Less pressure', ar: 'ضغط أقل' } },
      { id: 'nothing', text: { en: "I wouldn't change anything", ar: 'ما تغيرتش أي حاجة' } },
    ],
  },
  {
    id: '36q-life-chapter', category: 'intimate',
    text: { en: 'Which chapter of your life would you tell first?', ar: 'أنهي فصل من حياتك تحب تحكيه الأول؟' },
    options: [
      { id: 'childhood', text: { en: 'Childhood', ar: 'الطفولة' } },
      { id: 'move', text: { en: 'A big move', ar: 'نقلة كبيرة' } },
      { id: 'mistake', text: { en: 'A mistake that shaped me', ar: 'غلطة شكّلتني' } },
      { id: 'love', text: { en: 'My first love', ar: 'أول حب' } },
      { id: 'recent', text: { en: 'The past year', ar: 'آخر سنة' } },
    ],
  },
  {
    id: '36q-new-ability', category: 'intimate',
    text: { en: 'Wake tomorrow with one new ability — which?', ar: 'لو صحيت بكرة بقدرة جديدة، تختار إيه؟' },
    options: [
      { id: 'empathy', text: { en: 'Read people deeply', ar: 'أفهم الناس أكتر' } },
      { id: 'discipline', text: { en: 'Iron discipline', ar: 'انضباط ذاتي' } },
      { id: 'courage', text: { en: 'Courage to decide', ar: 'شجاعة في القرارات' } },
      { id: 'creativity', text: { en: 'Endless creativity', ar: 'إبداع وأفكار جديدة' } },
      { id: 'patience', text: { en: 'Patience and calm', ar: 'صبر وحلم' } },
    ],
  },
  {
    id: '36q-crystal-ball', category: 'intimate',
    text: { en: 'If a crystal ball gave you one truth — ask what?', ar: 'لو كرة بلّورية تقولك حقيقة واحدة، تسأل إيه؟' },
    options: [
      { id: 'death', text: { en: 'When will I die', ar: 'إمتى هموت' } },
      { id: 'relationship', text: { en: 'Will we last', ar: 'هل علاقتنا هتدوم' } },
      { id: 'meaning', text: { en: "My life's meaning", ar: 'معنى حياتي' } },
      { id: 'success', text: { en: 'Will I succeed at this thing', ar: 'هل هنجح في اللي بعمله' } },
      { id: 'nothing', text: { en: "I'd rather not know", ar: 'أفضل ما أعرفش' } },
    ],
  },
  {
    id: '36q-long-dream', category: 'intimate',
    text: { en: "Long-time dream — why haven't you done it?", ar: 'حاجة بتحلم بيها من زمان — ليه ما عملتهاش لسه؟' },
    options: [
      { id: 'resources', text: { en: 'Money or time', ar: 'محتاج فلوس أو وقت' } },
      { id: 'fear', text: { en: 'Fear of failing', ar: 'خايف من الفشل' } },
      { id: 'family', text: { en: 'Family obligations', ar: 'ظروف العيلة' } },
      { id: 'know-how', text: { en: "Don't know where to start", ar: 'مش عارف أبدأ منين' } },
      { id: 'soon', text: { en: "I've started already", ar: 'بدأت فيها فعلاً' } },
    ],
  },
  {
    id: '36q-greatest', category: 'intimate',
    text: { en: 'Greatest accomplishment of your life so far?', ar: 'أعظم إنجاز في حياتك لحد دلوقتي؟' },
    options: [
      { id: 'love', text: { en: 'The relationships I built', ar: 'العلاقات الحقيقية اللي بنيتها' } },
      { id: 'career', text: { en: 'A career win', ar: 'إنجاز في الشغل' } },
      { id: 'growth', text: { en: 'Coming through a crisis', ar: 'تخطّيت أزمة كبيرة' } },
      { id: 'kindness', text: { en: 'An act of kindness', ar: 'موقف ساعدت فيه حد' } },
      { id: 'education', text: { en: 'Education or a degree', ar: 'تعليم أو شهادة' } },
    ],
  },
  {
    id: '36q-friendship-value', category: 'intimate',
    text: { en: 'What do you value most in a friendship?', ar: 'أكتر حاجة بتقدّرها في الصداقة؟' },
    options: [
      { id: 'loyalty', text: { en: 'Loyalty', ar: 'الوفاء والثبات' } },
      { id: 'honesty', text: { en: 'Honesty without filter', ar: 'صراحة بدون فلتر' } },
      { id: 'fun', text: { en: 'Genuinely fun together', ar: 'إنك تضحك معاه' } },
      { id: 'depth', text: { en: 'Talking about anything', ar: 'تتكلموا في كل حاجة' } },
      { id: 'support', text: { en: 'Standing by you when it counts', ar: 'وقفته جنبك في الصعب' } },
    ],
  },
  {
    id: '36q-treasured-memory', category: 'intimate',
    text: { en: 'Your most treasured memory?', ar: 'أعزّ ذكرى عندك؟' },
    options: [
      { id: 'family', text: { en: 'A family moment', ar: 'لحظة عيلة' } },
      { id: 'partner', text: { en: 'A moment with my partner', ar: 'لحظة مع شريكي' } },
      { id: 'trip', text: { en: 'A trip I still replay', ar: 'رحلة فاكرها' } },
      { id: 'win', text: { en: 'A personal win', ar: 'إنجاز شخصي' } },
      { id: 'childhood', text: { en: 'A childhood memory', ar: 'حاجة من الطفولة' } },
    ],
  },
  {
    id: '36q-terrible-memory', category: 'intimate',
    text: { en: 'Your most terrible memory?', ar: 'أصعب ذكرى مرّت بيك؟' },
    options: [
      { id: 'loss', text: { en: 'Losing someone close', ar: 'فقد حد قريب' } },
      { id: 'betray', text: { en: 'A betrayal', ar: 'خيانة من حد ثقت فيه' } },
      { id: 'fear', text: { en: 'A moment of real fear', ar: 'موقف خوفت فيه قوي' } },
      { id: 'regret', text: { en: 'A regret I still carry', ar: 'غلطة لسه نادم عليها' } },
      { id: 'skip', text: { en: "I'd rather not share", ar: 'مش حابب أشاركها' } },
    ],
  },
  {
    id: '36q-one-year-left', category: 'intimate',
    text: { en: 'One year left to live — change how you live?', ar: 'لو معاك سنة واحدة بس في الدنيا، هتغيّر حاجة؟' },
    options: [
      { id: 'family', text: { en: 'More time with family', ar: 'أقعد مع عيلتي أكتر' } },
      { id: 'travel', text: { en: 'See the world', ar: 'أسافر وأشوف الدنيا' } },
      { id: 'risk', text: { en: "Things I'd been afraid to do", ar: 'حاجات كنت بخاف منها' } },
      { id: 'work', text: { en: 'Quit my job', ar: 'أسيب الشغل' } },
      { id: 'same', text: { en: "No — my life is good as is", ar: 'لا، حياتي حلوة زي ما هي' } },
    ],
  },
  {
    id: '36q-friendship-meaning', category: 'intimate',
    text: { en: 'What does friendship mean to you?', ar: 'الصداقة معناها إيه عندك؟' },
    options: [
      { id: 'trust', text: { en: 'Trust without doubt', ar: 'ثقة من غير شك' } },
      { id: 'fun', text: { en: 'Joy and laughter', ar: 'ضحك ومتعة' } },
      { id: 'presence', text: { en: 'Being there when needed', ar: 'إن حد موجود لما تحتاجه' } },
      { id: 'growth', text: { en: 'Growing together', ar: 'تكبروا مع بعض' } },
      { id: 'all', text: { en: 'All of it', ar: 'كل ده مع بعض' } },
    ],
  },
  {
    id: '36q-love-roles', category: 'intimate',
    text: { en: 'What role do love and affection play in your life?', ar: 'الحب والحنان دورهم إيه في حياتك؟' },
    options: [
      { id: 'center', text: { en: 'They are the centre', ar: 'محور كل حاجة' } },
      { id: 'important', text: { en: 'Important but not everything', ar: 'مهم بس مش الأساس' } },
      { id: 'complicated', text: { en: 'Honestly complicated', ar: 'علاقتي بيهم معقدة' } },
      { id: 'learning', text: { en: "I'm still learning", ar: 'لسه بتعلم' } },
    ],
  },
  {
    id: '36q-partner-best', category: 'intimate',
    text: { en: 'The single best thing about your partner?', ar: 'أحلى حاجة في شريكك؟' },
    options: [
      { id: 'kindness', text: { en: 'Their kindness', ar: 'قلبهم الطيب' } },
      { id: 'humour', text: { en: 'Their humour', ar: 'خفة دمهم' } },
      { id: 'mind', text: { en: 'Their mind', ar: 'عقلهم' } },
      { id: 'drive', text: { en: 'Their drive', ar: 'طموحهم' } },
      { id: 'presence', text: { en: 'Just having them around', ar: 'إنهم موجودين معايا' } },
    ],
  },
  {
    id: '36q-family-close', category: 'intimate',
    text: { en: 'How close is your family?', ar: 'عيلتك قريبة منك قد إيه؟' },
    options: [
      { id: 'very', text: { en: 'Very close', ar: 'قريبين قوي' } },
      { id: 'enough', text: { en: 'Close enough', ar: 'قريبين بدرجة كافية' } },
      { id: 'distant', text: { en: 'There is distance', ar: 'فيه بعد' } },
      { id: 'complicated', text: { en: "It's complicated", ar: 'علاقة معقدة' } },
    ],
  },
  {
    id: '36q-mother', category: 'intimate',
    text: { en: 'How is your relationship with your mother?', ar: 'علاقتك بأمك إزاي؟' },
    options: [
      { id: 'close', text: { en: 'Very close', ar: 'قريبين قوي' } },
      { id: 'healthy', text: { en: 'Healthy and balanced', ar: 'صحية ومتزنة' } },
      { id: 'complicated', text: { en: 'Complicated', ar: 'معقدة' } },
      { id: 'distant', text: { en: 'Distant', ar: 'بعيدة' } },
      { id: 'gone', text: { en: 'She has passed', ar: 'ربنا يرحمها' } },
    ],
  },
  {
    id: '36q-we-now', category: 'intimate',
    text: { en: 'Right now we are both…', ar: 'دلوقتي إحنا الاتنين…' },
    options: [
      { id: 'grateful', text: { en: 'Grateful', ar: 'ممتنين' } },
      { id: 'hopeful', text: { en: 'Hopeful', ar: 'متفائلين' } },
      { id: 'tired', text: { en: 'Tired', ar: 'تعبانين' } },
      { id: 'in-love', text: { en: 'In love', ar: 'حبايب' } },
      { id: 'curious', text: { en: 'Curious about each other', ar: 'متشوقين نعرف بعض أكتر' } },
    ],
  },
  {
    id: '36q-wish-share', category: 'intimate',
    text: { en: 'I wish I had someone to share my…', ar: 'نفسي يكون عندي حد أشارك معاه…' },
    options: [
      { id: 'thoughts', text: { en: 'Thoughts', ar: 'أفكاري' } },
      { id: 'fears', text: { en: 'Fears', ar: 'مخاوفي' } },
      { id: 'silence', text: { en: 'Silence', ar: 'صمتي' } },
      { id: 'adventures', text: { en: 'Adventures', ar: 'مغامراتي' } },
      { id: 'faith', text: { en: 'Faith', ar: 'إيماني' } },
    ],
  },
  {
    id: '36q-partner-knows', category: 'intimate',
    text: { en: 'Most important thing for your partner to know about you?', ar: 'أهم حاجة لازم شريكك يعرفها عنك؟' },
    options: [
      { id: 'insecurities', text: { en: 'My insecurities', ar: 'ضعفي ومخاوفي' } },
      { id: 'goals', text: { en: 'My dreams', ar: 'أحلامي وطموحاتي' } },
      { id: 'past', text: { en: 'My past', ar: 'تاريخي القديم' } },
      { id: 'love-lang', text: { en: 'My love language', ar: 'لغة الحب بتاعتي' } },
      { id: 'family', text: { en: 'My family ties', ar: 'علاقتي بعيلتي' } },
    ],
  },
  {
    id: '36q-like-partner', category: 'intimate',
    text: { en: 'Most honest thing you like about your partner?', ar: 'أصدق حاجة بتحبها في شريكك؟' },
    options: [
      { id: 'mind', text: { en: 'Their mind', ar: 'عقلهم' } },
      { id: 'heart', text: { en: 'Their heart', ar: 'قلبهم' } },
      { id: 'humour', text: { en: 'Their humour', ar: 'خفة دمهم' } },
      { id: 'presence', text: { en: 'Their presence', ar: 'وجودهم في حياتي' } },
      { id: 'drive', text: { en: 'What they achieve', ar: 'اللي بيحققوه' } },
    ],
  },
  {
    id: '36q-embarrassing', category: 'intimate',
    text: { en: 'Embarrassing moment you can laugh at now?', ar: 'موقف محرج لسه بتضحك عليه دلوقتي؟' },
    options: [
      { id: 'silly', text: { en: 'A silly slip', ar: 'غلطة سخيفة' } },
      { id: 'public', text: { en: 'In front of a crowd', ar: 'قدام الناس' } },
      { id: 'crush', text: { en: 'In front of a crush', ar: 'قدام حد كنت معجب بيه' } },
      { id: 'childhood', text: { en: 'From childhood', ar: 'من أيام الطفولة' } },
      { id: 'work', text: { en: 'At work', ar: 'في الشغل' } },
    ],
  },
  {
    id: '36q-last-cried', category: 'intimate',
    text: { en: 'When did you last cry in front of someone?', ar: 'آخر مرة عيّطت قدام حد إمتى؟' },
    options: [
      { id: 'today', text: { en: 'Very recently', ar: 'قريب جداً' } },
      { id: 'month', text: { en: 'This month', ar: 'في الشهر ده' } },
      { id: 'year', text: { en: 'A while ago', ar: 'من فترة' } },
      { id: 'rare', text: { en: 'Almost never', ar: 'نادراً ما يحصل' } },
      { id: 'alone', text: { en: 'I cry alone, not in front of people', ar: 'بعيّط لوحدي، مش قدام حد' } },
    ],
  },
  {
    id: '36q-first-noticed', category: 'intimate',
    text: { en: 'First thing you noticed about your partner?', ar: 'أول حاجة لفتتك في شريكك؟' },
    options: [
      { id: 'smile', text: { en: 'Their smile', ar: 'ابتسامتهم' } },
      { id: 'eyes', text: { en: 'Their eyes', ar: 'عيونهم' } },
      { id: 'energy', text: { en: 'Their energy', ar: 'طاقتهم' } },
      { id: 'voice', text: { en: 'Their voice', ar: 'صوتهم' } },
      { id: 'kindness', text: { en: 'Their kindness', ar: 'طيبتهم' } },
    ],
  },
  {
    id: '36q-too-serious', category: 'intimate',
    text: { en: 'What is too serious to joke about?', ar: 'إيه اللي تحس إنه أكبر من الهزار؟' },
    options: [
      { id: 'death', text: { en: 'Death', ar: 'الموت' } },
      { id: 'losses', text: { en: 'Family losses', ar: 'فقد العيلة' } },
      { id: 'religion', text: { en: 'Religion', ar: 'الدين' } },
      { id: 'mental', text: { en: 'Mental health', ar: 'الصحة النفسية' } },
      { id: 'nothing', text: { en: 'Nothing — laughter heals', ar: 'مفيش، الضحك علاج' } },
    ],
  },
  {
    id: '36q-die-tonight', category: 'intimate',
    text: { en: 'Dying tonight — biggest thing you have not said?', ar: 'لو مت الليلة، إيه أكبر حاجة معلقة عاوز تقولها؟' },
    options: [
      { id: 'love', text: { en: 'Love I never voiced', ar: 'حب ما عبّرتش عنه' } },
      { id: 'gratitude', text: { en: 'Gratitude I owe', ar: 'شكر ما قلتوش' } },
      { id: 'apology', text: { en: 'An apology I owe', ar: 'اعتذار لازم' } },
      { id: 'secret', text: { en: 'A secret I carry', ar: 'سر بحمله' } },
      { id: 'nothing', text: { en: 'Nothing — I say what I mean', ar: 'مفيش، بقول كل اللي عاوز أقوله' } },
    ],
  },
  {
    id: '36q-house-fire', category: 'intimate',
    text: { en: 'House on fire — one item to grab?', ar: 'البيت بيتحرق، تشيل حاجة واحدة معاك إيه؟' },
    options: [
      { id: 'photos', text: { en: 'Old photo album', ar: 'ألبوم الصور القديم' } },
      { id: 'documents', text: { en: 'Important papers', ar: 'الأوراق الرسمية' } },
      { id: 'gift', text: { en: 'A meaningful gift', ar: 'هدية لها معنى عندي' } },
      { id: 'journal', text: { en: 'A personal journal', ar: 'مذكرة شخصية' } },
      { id: 'phone', text: { en: 'My phone — it has everything', ar: 'الموبايل، فيه كل حاجة' } },
    ],
  },
  {
    id: '36q-loss-disturbing', category: 'intimate',
    text: { en: 'Whose loss would shake you the most?', ar: 'فقد مين هيهزّك أكتر؟' },
    options: [
      { id: 'parent', text: { en: 'A parent', ar: 'والد/والدة' } },
      { id: 'sibling', text: { en: 'A sibling', ar: 'أخ/أخت' } },
      { id: 'partner', text: { en: 'My partner', ar: 'شريكي' } },
      { id: 'child', text: { en: 'A child', ar: 'ابن/بنت' } },
      { id: 'friend', text: { en: 'A close friend', ar: 'صاحب قريب' } },
    ],
  },
  {
    id: '36q-advice-needed', category: 'intimate',
    text: { en: 'What kind of advice helps you most when struggling?', ar: 'أنهي نوع نصايح بتساعدك لما تكون متضايق؟' },
    options: [
      { id: 'listen', text: { en: 'Just listen', ar: 'مين يسمعني بس' } },
      { id: 'practical', text: { en: 'Practical steps', ar: 'خطوات عملية' } },
      { id: 'distract', text: { en: 'Distract me a bit', ar: 'إلهاء وضحك' } },
      { id: 'perspective', text: { en: 'A new perspective', ar: 'وجهة نظر تانية' } },
      { id: 'alone', text: { en: 'None — I solve alone', ar: 'مفيش، باحل لوحدي' } },
    ],
  },
]
export default BANK
